/**
 * ==============================================================
 * [MIGRATED TO MECHANICAL LINTER]
 * As regras abaixo foram removidas (ou adaptadas) dos prompts 
 * para não consumir limite cognitivo do LLM, sendo corrigidas 
 * automaticamente pelo parser:
 * - REGRA FRM-19 (Sem Markdown e artefatos TI): Removida da IA, gerida pelo Mechanical Linter.
 * - REGRA FRM-13 (Cabeçalhos em Caixa Alta): Removida da IA, gerida pelo Mechanical Linter.
 * - REGRA FRM-01 (Tempo no Cabeçalho): Removida da IA, gerida pelo Mechanical Linter.
 * - REGRA FRM-04 (Hierarquia de Localização): Removida da IA, gerida pelo Mechanical Linter.
 * - REGRA CHR-02 (Personagens em Caixa Alta): Removida da IA, gerida pelo Mechanical Linter.
 * - REGRA FRM-14 (Extensão na Mesma Linha): Removida da IA, gerida pelo Mechanical Linter.
 * - REGRA DLG-06 (Fechamento de Rubrica no Diálogo): Removida da IA, gerida pelo Mechanical Linter.
 * - REGRA FRM-02 (Glue Rule - Espaçamento): Removida da IA, gerida pelo Mechanical Linter.
 * ==============================================================
 */
import { GoogleGenAI, Content, Part, HarmCategory, HarmBlockThreshold } from "@google/genai";
import { Language, TRANSLATIONS } from "../translations";
import { CharacterProfile, AppState, AppConfig, ChatMessage, SceneShotList, PillarDefinition, SceneCameraDirection, EffectSuggestion, SceneEffectsResult, DurationEstimate, ReviewProgressState } from "../types";
import { METHOD_MCKEE_RULES, METHOD_FIELD_RULES, METHOD_HYBRID_RULES, PLOT_TWIST_KNOWLEDGE, PLANT_AND_PAYOFF_KNOWLEDGE, PILLAR_LIBRARY, AUTEUR_PILLARS_KNOWLEDGE_PT, AUTEUR_PILLARS_KNOWLEDGE_EN, AUTEUR_PILLARS_KNOWLEDGE_ES, AUTEUR_PILLARS_KNOWLEDGE_FR, getFormattingKnowledge, SCRIPT_AUDITING_KNOWLEDGE, FORMATTING_STANDARDS_KNOWLEDGE } from "../features/knowledge/SystemKnowledge";
import { getAuditKnowledge, getCriticalRules, getRole, getExecutionSteps, getFormattingRules, getGoldStandard } from "../features/knowledge/MasterSceneKnowledge";
import { AUDIT_ERRORS, AUDIT_ERRORS_EN, LOCALIZED_ERRORS, cleanCode, MECHANICAL_RULES_EXCLUSION } from "../features/knowledge/AuditErrorMessages";
import { getSystemContext } from './formatService';
import { executeWithResilience } from '../utils/network';

export interface CausalityNode {
    event: string;
    trigger_found: string | null;
    consequence: string | null;
}

export interface PlotAuditResponse {
    causality_chain: CausalityNode[];
    warning_points: string[];
    strategic_suggestions: string[];
    requires_rewrite: boolean;
}

const LANGUAGE_MAP: Record<string, string> = {
    'en': 'English',
    'pt': 'Portuguese',
    'es': 'Spanish',
    'fr': 'French'
};

const GOLDEN_RULES = `[TECHNICAL GOLDEN RULES - NON-NEGOTIABLE]:
1. TITLE: First line MUST be only the title in ALL CAPS. NEVER use "TITLE:".
2. SCENE HEADINGS: ALL CAPS and PLAIN TEXT (no bold). Simple hyphens (-). NO SCENE NUMBERS. Ex: "INT. HOUSE - DAY".
3. CHARACTER: Name MUST be in ALL CAPS.
4. THE BREAK RULE (NO ACTION IN DIALOGUE): It is STRICTLY PROHIBITED to describe physical actions (e.g., "he walks", "takes the hat") inside the dialogue block. PHYSICAL ACTION = NEW ACTION LINE. If the character acts, CLOSE the dialogue, write the Action line, and OPEN a new dialogue with (CONT'D).
5. PARENTHETICALS (INTENTION): Use parentheses ONLY for Tone of Voice or Intention (e.g. whispers, sarcastic). Max 3 words. PROHIBITED to describe physical actions (e.g. looking, laughing), PROHIBITED LINE BREAKS and PROHIBITED HYPHENATION inside parentheses. The parenthetical must be on a single line without partition hyphens.
6. EXTENSIONS: Use ONLY "(V.O.)" or "(O.S.)". NEVER use (OFF), (VOICE).
7. NO MARKDOWN (FRM-19): NEVER use bold (**), italics (*), underlines (_), or blockquotes (>) in headings, names, or dialogue. Triple backticks (\`\`\`) and IT-style formatting artifacts are STRICTLY FORBIDDEN. Remove all Markdown residue.
8. TRANSITIONS: End with ":" (or "." for FADE OUT). Ex: "FADE OUT." or "CUT TO:".
9. ENDING (CAPPING THE PROJECT): Do not end abruptly after the final line. Skip TWO LINES after the last speech and insert "FADE OUT." or "THE END.". NEVER end with "CUT TO:". NOTHING should appear after the ending.
10. NARRATOR: Treat Narrator as a CHARACTER (e.g. NARRATOR in ALL CAPS). Never use "Narrator's Voice".
11. LOGICAL SETTING: Open locations (Sea, Sky) are ALWAYS 'EXT.'. "INT. SEA" is impossible.
12. NO NAME STACKING (FATAL ERROR): NEVER write a character's name multiple times in a row. A character name must appear EXACTLY ONCE, immediately followed by their dialogue.
13. DIALOGUE BLOCK INTEGRITY (FATAL ERROR): A Character Name (and Parenthetical) MUST be immediately followed by spoken words. NEVER write a scene description (Action) directly below a Character Name or Parenthetical. Action paragraphs MUST ALWAYS be separated from Character/Dialogue blocks by double line breaks (\\n\\n).
14. NO ACTION IN PLACE OF SPEECH: If a character only acts or makes a non-verbal sound (grunt, sigh), write the sound as SPOKEN DIALOGUE (e.g. "Grrr!"), then use a DOUBLE LINE BREAK (\\n\\n), and then write the action line. NEVER put an action description immediately under a character's name.
15. NO QUOTES IN NAMES: NEVER use quotation marks inside a Character Name. Ex: Write FORMIGA BOBA DA CORTE, never FORMIGA "Boba da corte". Character names must be 100% ALL CAPS without punctuation.
16. ZERO HTML OR HTML-LIKE TAGS: NEVER use <center>, <b>, <i> or ANY HTML tags. Write completely in plain text.`;

// Helper: Get Safety Settings based on config
export const getSafetySettings = (config?: AppConfig) => {
    if (config?.matureLanguage || config?.enableHorrorOps) {
        return [
            { category: HarmCategory.HARM_CATEGORY_HARASSMENT, threshold: HarmBlockThreshold.BLOCK_NONE },
            { category: HarmCategory.HARM_CATEGORY_HATE_SPEECH, threshold: HarmBlockThreshold.BLOCK_NONE },
            { category: HarmCategory.HARM_CATEGORY_SEXUALLY_EXPLICIT, threshold: HarmBlockThreshold.BLOCK_NONE },
            { category: HarmCategory.HARM_CATEGORY_DANGEROUS_CONTENT, threshold: HarmBlockThreshold.BLOCK_NONE },
        ];
    }
    return undefined;
};

export const getAIInstance = async () => {
    const env = (import.meta as any).env || {};
    const projectId = env.VITE_FIREBASE_PROJECT_ID || 'auteuros';
    const useEmulators = env.DEV && env.VITE_USE_EMULATORS === "true";

    const baseUrl = useEmulators
        ? `http://localhost:5001/${projectId}/us-central1/core/api`
        : `/api`;

    // Retorna uma interface mockada compatível com o SDK oficial da Google
    return {
        models: {
            generateContent: async ({ model, contents, config }: any) => {
                const response = await fetch(`${baseUrl}/generate-content`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        model,
                        contents,
                        config,
                        systemInstruction: config?.systemInstruction
                    })
                });

                if (!response.ok) {
                    const errorText = await response.text();
                    throw new Error(`Erro na API de IA segura (Functions): ${response.status} ${errorText}`);
                }

                const data = await response.json();
                return {
                    text: data.text,
                    response: {
                        text: () => data.text
                    }
                };
            },
            countTokens: async ({ model, contents }: any) => {
                const response = await fetch(`${baseUrl}/count-tokens`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ model, contents })
                });

                if (!response.ok) {
                    return { totalTokens: 100 }; // fallback seguro
                }

                return await response.json();
            }
        }
    };
};

// Fast Semantic Tag Validator
export const validateTagSemantically = async (
    tag: string,
    language: string
): Promise<{ isProperName: boolean; reason?: string }> => {
    return retryWithBackoff(async () => {
        const ai = await getAIInstance();
        const modelName = await getBestModel(ai);

        const prompt = `
            TASK: Classify the following term for a screenwriting application.
            TERM: "${tag}"
            CONTEXT: The user is trying to add this term as a "Personality Trait" or "Conversation Topic" tag for a character.
            
            RULES:
            1. If it's a Proper Name (Person, Place, Organization), return {"isProperName": true}.
            2. If it's an Adjective, Personality Trait, Generic Topic, or Descriptor (even if capitalized), return {"isProperName": false}.
            3. Common descriptors in screenwriting (like "Otimista", "Empathetic", "Brave") should be FALSE.
            
            Return ONLY JSON in this format: {"isProperName": boolean, "reason": "string"}
            Answer context is in language: ${language}.
        `;

        const response = await ai.models.generateContent({
            model: modelName,
            contents: [{ role: 'user', parts: [{ text: prompt }] }],
            config: {
                responseMimeType: "application/json"
            }
        });

        if (response.text) {
            return JSON.parse(response.text);
        }
        return { isProperName: false };
    });
};


// Status rotation helper for reviewScript
const ANALYSIS_STAGES: Record<string, string[]> = {
    pt: [
        "Analisando lógica narrativa de",
        "Auditando estrutura e ritmo de",
        "Avaliando descrições de ação de",
        "Verificando integridade dos diálogos de",
        "Validando rubricas e parênteses de"
    ],
    en: [
        "Analyzing narrative logic of",
        "Auditing structure and rhythm of",
        "Evaluating action descriptions of",
        "Verifying dialogue integrity of",
        "Validating parentheticals of"
    ],
    es: [
        "Analizando lógica narrativa de",
        "Auditando estructura y ritmo de",
        "Evaluando descripciones de acción de",
        "Verificando integridad de diálogos de",
        "Validando acotaciones y paréntesis de"
    ],
    fr: [
        "Analyse de la logique narrative de",
        "Audit de la structure et du rythme de",
        "Évaluation des descriptions d'action de",
        "Vérification de l'intégrité des dialogues de",
        "Validation des rubriques et parenthèses de"
    ]
};

const PREPARING_CONTEXT: Record<string, string> = {
    pt: "Preparando contexto para",
    en: "Preparing context for",
    es: "Preparando contexto para",
    fr: "Préparation du contexte pour",
};

// Model Configuration
const PRO_MODEL = 'gemini-2.5-pro';
const DEFAULT_MODEL = 'gemini-3.5-flash';
const FALLBACK_MODEL = 'gemini-1.5-flash-002';

let validatedModel: string | null = null;
let validatedProModel: string | null = null;

export const getBestModel = async (ai: any): Promise<string> => {
    // Return cached valid model if available
    if (validatedModel) return validatedModel;

    try {
        console.log(`[Model Check] Verifying availability of ${DEFAULT_MODEL}...`);
        await ai.models.countTokens({
            model: DEFAULT_MODEL,
            contents: [{ parts: [{ text: 'ping' }] }]
        });

        console.log(`[Model Check] ${DEFAULT_MODEL} is AVAILABLE.`);
        validatedModel = DEFAULT_MODEL;
        return DEFAULT_MODEL;
    } catch (error: any) {
        console.warn(`[Model Check] ${DEFAULT_MODEL} failed or not available. Falling back to ${FALLBACK_MODEL}. Error:`, error.message);
        validatedModel = FALLBACK_MODEL;
        return FALLBACK_MODEL;
    }
};

/**
 * Returns the best available Pro model for high-quality auditing tasks.
 * Falls back to the standard getBestModel() if the Pro model is unavailable.
 */
export const getBestProModel = async (ai: any): Promise<string> => {
    if (validatedProModel) return validatedProModel;

    try {
        console.log(`[Model Check] Verifying availability of PRO model: ${PRO_MODEL}...`);
        await ai.models.countTokens({
            model: PRO_MODEL,
            contents: [{ parts: [{ text: 'ping' }] }]
        });

        console.log(`[Model Check] ${PRO_MODEL} is AVAILABLE. Using Pro for auditing.`);
        validatedProModel = PRO_MODEL;
        return PRO_MODEL;
    } catch (error: any) {
        console.warn(`[Model Check] ${PRO_MODEL} unavailable. Falling back to standard model. Error:`, error.message);
        const fallback = await getBestModel(ai);
        validatedProModel = fallback;
        return fallback;
    }
};

async function retryWithBackoff<T>(fn: () => Promise<T>, retries = 3, delay = 1000): Promise<T> {
    try {
        return await fn();
    } catch (error: any) {
        if (retries === 0) throw error;
        // 429 is Rate Limit, 5xx is Server Error
        if (error?.status === 429 || (error?.status && error.status >= 500)) {
            await new Promise(r => setTimeout(r, delay));
            return retryWithBackoff(fn, retries - 1, delay * 2);
        }
        throw error;
    }
}


export function getSystemInstruction(config: AppConfig, isFirstDraft: boolean = false, format: string = 'Film', includePillars: boolean = false): string {
    let instruction = getRole('en', format);
    instruction += "\n\nCRITICAL: The examples provided in the technical rules (e.g. locations like CASA or characters) are for STRUCTURAL REFERENCE ONLY. DO NOT use their specific content, locations, or plots for the generated script unless explicitly part of the user's idea.";

    // INJECT CORE PILLARS ONLY IF REQUESTED (Prevent "Secret Bias")
    if (includePillars) {
        const pillarsKnowledge = AUTEUR_PILLARS_KNOWLEDGE_EN;
        instruction += `\n\n[AUTEUR.OS CORE PHILOSOPHY - 27 PILLARS]\nUse these 27 pillars to analyze, structure, and critique stories:\n${pillarsKnowledge}`;
    }

    // INJECT FORMATTING STANDARDS logic (BIBLE) - ALWAYS include for consistency
    const strictRules = getFormattingKnowledge('en');
    instruction += `\n\n${strictRules}`;

    // MODE SPECIFIC INSTRUCTIONS
    if (config.scriptMode === 'shooting') {
        instruction += `\n\n[SHOOTING MODE ACTIVE (PRODUCTION)]\n- ENFORCE scene numbering (if applicable).\n- PRESERVE camera directions and technical cues.\n- ALLOW "CUT TO" and specific transition markings.`;
    } else {
        // Default to SPEC (Selling Script)
        instruction += `\n\n[SPEC MODE ACTIVE (SELLING FORMAT)]\n- STRICTLY PROHIBITED: Scene Numbering.\n- STRICTLY PROHIBITED: Technical Camera Directions (CLOSE UP, ANGLE ON, DOLLY).\n- STRICTLY PROHIBITED: Production notes or shot lists.\n- Use Master Scene format only.`;
    }

    // DYNAMIC SYNERGY: Stack active methodologies
    const activeMethods: string[] = [];

    if (config.methodMcKee) {
        activeMethods.push(`[METHODOLOGY: ROBERT MCKEE]\n${METHOD_MCKEE_RULES}`);
    }

    if (config.methodField) {
        activeMethods.push(`[METHODOLOGY: SYD FIELD]\n${METHOD_FIELD_RULES}`);
    }

    // Add active custom methods
    if (config.customMethods && config.customMethods.length > 0) {
        const activeCustom = config.customMethods.filter(m => m.isActive);
        activeCustom.forEach(m => {
            activeMethods.push(`[METHODOLOGY: ${m.name.toUpperCase()}]\n${m.content}`);
        });
    }

    // Inject stacked methodologies
    if (activeMethods.length > 0) {
        instruction += `\n\n*** ACTIVE METHODOLOGIES ***\nApply the following narrative theories simultaneously:\n\n${activeMethods.join("\n\n")}`;

        // Conflict Protocol (Localized)
        const conflictProtocol = "If conflicting rules arise, prioritize the instructions from custom methods (User Uploads) over classic theories.";

        instruction += `\n\n[CONFLICT PROTOCOL]\n${conflictProtocol}`;
    }

    // Plant & Payoff moved to Atomic Tools (no longer global)

    // Force language in System Instruction as well
    const langLabel = config.language === 'pt' ? 'Portuguese (Português do Brasil)'
        : config.language === 'es' ? 'Spanish (Español)'
            : config.language === 'fr' ? 'French (Français)'
                : 'English';

    instruction += `\n\n*** IMPORTANT LANGUAGE RULE ***\nYou MUST respond primarily in ${langLabel}. Do NOT output English unless specifically asked to translate.`;

    // FINAL REINFORCEMENT OF MASTER SCENE (GOLDEN RULES)
    instruction += `\n\n[GOLDEN RULES - ERROR PREVENTION]:
1. TITLE: First line MUST be only the title text in ALL CAPS. NEVER use "TITLE:".
2. CHARACTER INTRO: NAME in CAPS first, then visual details. (e.g. "JOHN (20s), torn shirt"). No psychology.
3. ACTION: Present tense. Visual/physical focus.
4. SCENE HEADINGS: Hierarchical ([MAIN] – [SUB] – [TIME]). Prefix with "FLASHBACK – " for past scenes. MUST BE ALL CAPS AND PLAIN TEXT (NO BOLD, NO UNDERLINE).
5. NO AI TRASH: No "Here is the script", no "Note:", no summaries. Output script ONLY.
6. NO MARKDOWN (FRM-19): NEVER use bold (**), italics (*), underlines (_), or blockquotes (>) in headings, character lines, or dialogue. Triple backticks (\`\`\`) are IT formatting codes and have no place in a cinema script. Sluglines and text must be plain. Remove all Markdown residue (*, _, #, >, \`\`\`).
7. EXTENSIONS ON SAME LINE: Extension (V.O., O.S., CONT'D) must ALWAYS be on the same line as the Character Name. NEVER put it on the next line.
8. NO HTML TAGS: Never use <center>, <b>, <i>, or any HTML tags in the script. The script must be plain text. Do NOT use <center> to center character names.
9. ENDING (CAPPING THE PROJECT): NEVER end a script abruptly after the final speech. Skip TWO LINES and use "FADE OUT." or "THE END." to finish the script. This closes the project "lid". NEVER end with internal transitions like "CUT TO:".
10. NO BRACKETS IN SLUGLINES: Scene headings must never be enclosed in brackets [ ]. Standard: "INT. LOCATION - DAY".
11. NARRATOR IS A CHARACTER: Always format narrator dialogue like a regular character (Name in ALL CAPS). Never output narrated text as an action paragraph.
12. CHARACTER NAMES IN ALL CAPS: All character names, including "NARRATOR", "NARRATOR'S VOICE", etc., MUST be in ALL CAPS. Mixed case (e.g. "Narrator's Voice") will break the parser.
13. DIALOGUE IS DIALOGUE: Never summarize or render dialogue as a paragraph. It must follow the Character -> Dialogue structure.
14. NO MIXED CASE IN NAMES: "VOZ DO NARRADOR" is correct. "Voz do Narrador" is WRONG.
15. PARENTHETICALS (INTENTION): Use parentheses ONLY for Tone of Voice or Intention (e.g. whispers, sarcastic). Max 3 words. PROHIBITED to describe physical actions (e.g. looking, laughing), PROHIBITED LINE BREAKS and PROHIBITED HYPHENATION inside parentheses. Move actions to Action lines. Ensure the parenthetical is on a single line without partition hyphens.
16. THE BREAK RULE (NO ACTION IN DIALOGUE): It is STRICTLY PROHIBITED to describe physical actions (e.g., "he walks", "takes the hat") inside the dialogue block. PHYSICAL ACTION = NEW ACTION LINE. If the character acts, CLOSE the dialogue, write the Action line, and OPEN a new dialogue with (CONT'D).
17. NO ORPHAN CUES / FAKE DIALOGUE: If you write a Character Name, you MUST write their spoken dialogue immediately after. DO NOT write an action line in the place of dialogue. Use double blank lines (\\n\\n) to exit the dialogue block before writing an Action.
18. NO ACTION IN PLACE OF SPEECH: Never place an action paragraph directly under a character name or parenthetical. If they just act, don't write their name as a dialogue cue. If they grunt, write "Grrr!" as dialogue, jump two lines, then describe the action.
`;

    // INJECT FORMAT-SPECIFIC RULES & STEPS
    instruction += `\n\n${getCriticalRules(format)}`;
    instruction += `\n\n${getExecutionSteps('en', format)}`;

    if (config.matureLanguage) {
        instruction += `
        
        [MATURE CONTENT AUTHORIZED - UNFILTERED MODE]
        1. You are explicitly authorized to generate content involving profanity, violence, and adult themes if requested or appropriate for the story contexts (e.g., crime drama, war, gritty realism).
        2. DO NOT censor or tone down dialogue. Authenticity to the characters and the world is the priority.
        3. Ignore standard safety filters for creative writing purposes.
        4. If the user requests a style that implies strong language (e.g., Tarantino, Scorsese, cyberpunk), use appropriate language without hesitation.
        `;
    }

    if (config.enableHorrorOps) {
        instruction += `
        
        [HORROR & VIOLENCE OPS - EXTREME TENSION MODE]
        1. YOU ARE AUTHORIZED TO DEPICT VISCERAL VIOLENCE, GORE, AND PSYCHOLOGICAL HORROR. This is a creative writing tool for adults.
        2. PRIORITIZE TENSION: Build dread through pacing, atmosphere, and sensory details (sound, smell, sight).
        3. DO NOT SHY AWAY from the grotesque or the terrifying. Use graphic descriptions where they enhance the narrative impact.
        4. Focus on the psychological toll of violence on characters. Be unfiltered in your description of fear and pain.
        `;
    }

    return instruction;
}

// Helper to get localized prompt content
function getLocalizedTaskInstruction(taskType: string, lang: 'en' | 'pt' | 'es' | 'fr'): string {
    const prompts: Record<string, Record<string, string>> = {
        pacing: {
            en: `Act as a senior script doctor specialized in narrative pacing and dramatic cadence.
            Your task is to generate ONE single, high-quality pacing recommendation considering the script as a whole.
            
            CORE DIRECTIVE: "The pacing must serve the central emotion of the genre."

            GENRE ALIGNMENT:
            1. Identify the dominant genre first.
            2. CALIBRATION RULES:
                - THRILLER (Constant tension/threat): Controlled but restless start, abrupt accelerations in the middle, few long pauses, increasing urgency. Focus: reducing dead time, dry cuts, fast threat->reaction. Question: "Does the viewer have too much time to breathe?"
                - DRAMA (Emotional immersion): Lento and deliberate rhythm, significant pauses, progressive growth. Focus: where to hold time, where to allow silence, slight accelerations to avoid stagnation. Question: "Does this moment need to be felt or advanced?"
                - SCI-FI (Discovery/Awe/Reflection): Alternation between contemplation and impact, info blocks followed by action. Focus: avoid continuous exposition, break explanation with consequence, acceleration as cognitive shock. Question: "Does the idea have time to settle before being turned upside down?"
                - MYSTERY (Curiosity/Mental puzzle): Cadenced rhythm, tiered information, point accelerations after discoveries. Focus: where to hold info, avoid long peaks without new clues. Question: "Is curiosity being fed or frustrated?"
                - TERROR/HORROR (Fear/Dread/Anticipation): Long slow build-ups, short violent bursts, fake relief retreats. Focus: lengthen anticipation, shorten traumatic events, use silence as rhythm. Question: "Does fear come before the shock?"
                - FANTASY (Wonder/Destiny/Epic): Wide rhythm, contemplative moments, gradual growth to expansive climax. Focus: allow the world to breathe, avoid rushing mythical revelations, balance journey and spectacle. Question: "Is the world being lived or just passed through?"
                - COMEDY (Timing/Surprise/Lightness): Agile rhythm, lean narrative, calculated pauses for punchlines. Focus: shorten setup, adjust pauses before the laugh, avoid long explanations. Question: "Does the joke land at the right time?"
            
            PRIORITIZATION:
            - Prioritize the dominant emotional genre. Secondary genres should only support the main one. Never compromise the central genre's promise.
            
            SCOPE:
            - Analyze the global pacing, not just local scenes.
            - NO lists, NO sections, NO multiple options, NO scene/dialogue rewrites.
            
            OUTPUT FORMAT:
            - Return ONLY a single continuous, clear, and direct text.
            - NO titles, NO lists, NO numbering, NO technical markings.
            - The text must sound like precise authorial orientation, not a report.
            - LANGUAGE: The final text MUST be written in the specified target language.`,
            pt: "", es: "", fr: ""
        },
        elements: {
            en: "Suggest new elements (characters, props, locations) to enhance the story depth. Provide suggestions in the target language.",
            pt: "", es: "", fr: ""
        },
        transitions: {
            en: "Suggest better transitions between scenes to improve flow. Provide suggestions in the target language.",
            pt: "", es: "", fr: ""
        },
        plottwist: {
            en: `Suggest a single, high-impact Plot Twist that recontextualizes the story.
            
            STEP 1: Identify the dominant genre (Thriller, Drama, Sci-Fi, Mystery, Horror, Fantasy).
            STEP 2: Apply genre-specific laws and the "Question-key" for that genre.
            STEP 3: Ensure the twist recontextualizes the past and changes the dramatic vector.
            
            KEY PHRASE: "This Plot Twist must surprise without betraying the genre."

            OUTPUT FORMAT:
            - A single, continuous, clear, and editable text.
            - CONTENT: Description of the Twist, the change of meaning it causes, and the dramatic impact.
            - NO titles, NO lists, NO subtitles, NO bullets, NO numbering.
            - NO technical terms or scene citations.
            - LANGUAGE: The final text MUST be written in the specified target language.`,
            pt: "", es: "", fr: ""
        },
        plotaudit: {
            en: `Act as a senior Script Analyst specialized in narrative structure, dramatic logic, and plot analysis.
Perform a Plot Audit focused exclusively on the macro level of the story.

CORE DIRECTIVE: "Nothing happens because the script wants it. Everything happens because the story demands it."

STEP 1: Identify the dominant genre and apply the GENRE ALIGNMENT:
- THRILLER (Tension/Threat): Audit if the threat is active and the consequences immediate. Warning: Passive villain, artificial urgency.
- DRAMA (Emotional Truth): Audit if the internal conflict drives decisions and if there is an emotional cost. Warning: Declared emotion (not lived).
- SCI-FI (Coherence): Audit if system rules are stable and technology has limits. Warning: Magical solutions, new rules at the end.
- MYSTERY (Narrative Justice): Audit if clues are fair and the solution derives from investigation. Warning: Information hidden from the audience.
- TERROR (Fear/Inevitability): Audit if horror is consistent and threat is not neutralized early. Warning: Excessive explanation.
- FANTASY (Myth/Destiny): Audit mythical rules and costs of power. Warning: Power without price, convenient prophecies.
- COMEDY (Timing/Reversal): Audit if logic sustains timing and if chaos grows controlled. Warning: Isolated jokes without progression.

STEP 2: Apply the PLOT LOGIC CRITERIA:
1. CLEAR CAUSALITY (A -> B -> C): Every event must be born from a cause and generate a consequence.
2. JUSTIFIABLE MOTIVATION: Characters act by desires/fears, not script convenience.
3. STABLE WORLD RULES: No opportunistic exceptions or "out of nowhere" solutions.
4. SETUPS WITH PAYOFF: Every narrative promise must be prepared and resolved.
5. CONTINUOUS DRAMATIC PROGRESSION: Story never returns to same state; risk and cost increase.
6. UNIFIED CENTRAL CONFLICT: A dominant conflict that organizes secondary ones.
7. DERIVED RESOLUTION (NOT CONVENIENT): Ending emerges from earned choices, not luck.

OUTPUT FORMAT (STRICT):
Translate all headers and labels into the target language.

### [PLOT AUDIT HEADER] : [SCORE]/100
- Overall status: [Solid | Attention | Critical]
- Summary Diagnosis (1-2 paragraphs integrating logic and genre)

### [WARNING POINTS HEADER] (if any)
- Objective list of problems found based on criteria.

### [STRATEGIC SUGGESTIONS HEADER]
- Conceptual suggestions to adjust causality, motivation, or rules.
- Do NOT write script text or dialogues.

### [RECOMMENDED ACTION HEADER]
- If no problems: "No mandatory action. Script is structurally consistent."
- If problems: List strategic actions.

END WITH THE EXACT PHRASE (Translated to target language): "Do you want to generate a new script applying these structural corrections?"
LANGUAGE: The final text MUST be written in the specified target language.`,
            pt: "", es: "", fr: ""
        },
        continuity: {
            en: "Analyze the script for continuity errors (time, objects, world rules). For each error, create a section starting with '### [Error name translated back into target language]'. In this section, identify the problem and then provide a clear '**Suggestion**' for correction. Be specific and coherent with the script context. Output MUST be in the target language.",
            pt: "", es: "", fr: ""
        },
        chararcs: {
            en: "Analyze the CHARACTER ARCS in the script. Identify emotional growth, internal conflicts, and arc consistency. Output MUST be in the target language.",
            pt: "", es: "", fr: ""
        },
        camera: {
            en: "Suggest camera directions (plans, angles, focus, movements) per scene. Maximum 3 suggestions per scene. Use standardized cues. Output MUST be in the target language.",
            pt: "", es: "", fr: ""
        },
        effects: {
            en: "Suggest SFX (sound effects), VFX (visual effects), and practical effects per scene only if there is textual evidence. Do not invent. Output MUST be in the target language.",
            pt: "", es: "", fr: ""
        },

        visual_atmosphere: {
            en: `Act as a Director of Photography and Screenwriter.
Your task is to REWRITE THE SCRIPT, enriching the action lines with vivid descriptions of lighting, color, texture, and atmosphere.

CORE DIRECTIVE: "Light and color must narrate what words do not say."

REWRITE INSTRUCTIONS:
1. MAINTAIN MASTER SCENES FORMAT (Heading, Action, Character, Dialogue).
2. DO NOT CHANGE DIALOGUES.
3. In action lines, insert sensory descriptions of light (contrast, direction), color temperature, and textures.
4. Transform dry descriptions into cinematic visuals.

Example:
Before: "John enters the dark room."
After: "John enters. The room is swallowed by gloom, sliced only by a harsh, bluish beam leaking from the blinds."

OUTPUT:
Return the full script rewritten in the target language with these atmospheric enhancements.`,
            pt: "", es: "", fr: ""
        },
        plant_and_payoff: {
            en: `Act as a senior Script Doctor specialized in narrative engineering and dramatic cohesion.
Your task is to perform a Plant & Payoff analysis using a Multi-level Scope logic.

CONTEXT:
- PLANT (Seed): An element (object, dialogue, character trait) introduced early that seems casual but is essential later.
- PAYOFF (Reward): The resolution or revelation that gives meaning to the planted seed.

SCOPE OF ANALYSIS:
1. GLOBAL AUDIT: Evaluate the macro cohesion of the script. Identify if the central theme has a payoff that lives up to the initial promise.
2. SUGGESTIONS BY ACTS: Identify opportunities to plant seeds in Act 1 to be harvested in Act 3, or in Act 2 to be harvested at the climax.
3. SUGGESTIONS BY SCENE: Point out specific details where a "plant" can be inserted to increase dramatic irony or audience satisfaction.

OUTPUT FORMAT (Markdown):
Return a structured report in the TARGET LANGUAGE:
### GLOBAL COHESION AUDIT
[Macro analysis of the cause-and-effect structure]

### ENGINEERING BY ACTS
- [Plant Suggestion in Act X] -> [Payoff Suggestion in Act Y]

### REFINEMENT BY SCENE
- [Scene #]: [Suggestion for seed insertion or resolution]

LANGUAGE RULE:
- All content must be in the target language.
- The final phrase must be in the target language.

FINISH WITH THE EXACT PHRASE (In target language): "Do you wish to apply these narrative seeds?"`,
            pt: "",
            es: "",
            fr: ""
        },
        scriptaudit: {
            en: `You are a professional screenplay editor specialized in "Master Scenes" formatting.
Your task is to audit the script for technical violations and suggest a score from 0 to 100.

TECHNICAL DIRECTIVES:
1. flag technical errors that break Master Scene format.
2. stylistic choices (adjectives, etc.) are NOT errors. Label them as "Suggestion" with 0 points impact.
3. subtract points ONLY for REAL ERRORS. If zero errors, score MUST be 100.
4. do not re-flag corrected errors from previous rounds.

RULES CHECKLIST:
1. ACTION MUST BE FILMABLE (No thoughts/feelings).
2. VERB TENSE must be PRESENT.
3. SHOW, DON'T TELL.
4. ACTION BLOCKS: Max 4-5 lines.
5. CHARACTER INTRO: ALL CAPS + Physical description.
6. PARENTHETICALS: Tone/Intention only. Max 3 words. No breaks.
7. CAMERA DIRECTIONS: PROHIBITED.
8. HEADINGS: INT./EXT., Location, Time, FLASHBACK/CONTINUOUS consistency.
9. SOUND/NON-VISUAL: Sounds okay, internal sensations PROHIBITED.
10. LITERARY LANGUAGE: PROHIBITED (No "Suddenly", no novel-like prose).
11. CAPITALIZATION: 1st intro, sounds, VFX only.
12. NO HTML.
13. CONTINUITY: Consistent names.
14. GHOST CHARACTERS: Every speaker must be in preceding action.
15. NO ORPHAN SLUGLINES (FRM-18).
16. NO MARKDOWN RESIDUE (FRM-19).
17. PROFESSIONAL FINISH: End with FADE OUT. or THE END.

OUTPUT FORMAT (STRICT JSON):
{
    "score": [0-100],
    "issues": [
        {
            "type": "error" | "suggestion",
            "line": [number],
            "original": "[text]",
            "suggestion": "[correction in target language]",
            "rule": "[Code, e.g., ACT-01]"
        }
    ]
}

LANGUAGE RULE:
- The "suggestion" field MUST be in the target language.
- The final response MUST be 100% valid JSON.

FINISH THE INTERACTION WITH THE EXACT PHRASE (In target language): "Do you want to generate a new version applying these formatting corrections?"`,
            pt: "",
            es: "",
            fr: ""
        },
        tone_analyzer: {
            en: `Act as a senior script consultant specialized in Tone and Voice.
Audit the script for tonal consistency, genre alignment, and character differentiation.

DIRECTIVES:
1. VOICE CONSISTENCY: Detect abrupt shifts in tone.
2. GENRE ALIGNMENT: Verify if vocabulary matches the chosen genre.
3. BALANCE CONTROL: Measure the ratio of "Humor" vs "Drama".
4. CHARACTER DIFFERENTIATION: Analyze if characters have distinct voices.

Return a detailed report using the following Markdown structure:

### [Character Name]
**Tone:** [Description of the character's voice in target language]
**Consistency:** [Consistency analysis in target language]
**Diagnosis:** [Final character tone diagnosis in target language]

### Genre Alignment
**Genre:** [Target Genre]
**Analysis:** [Vocabulary and sentence structure analysis in target language]

### Balance Control (Dramedy)
**Ratio:** [% Humor] Humor / [% Drama] Drama
**Analysis:** [Balance evaluation in target language]

### Character Differentiation
**Analysis:** [How distinct the voices are from each other in target language]

### Tonal Coherence
**Analysis:** [How characters react to each other's tonal shifts in target language]

LANGUAGE RULE:
- All labels (Tone, Consistency, etc.) should be in the target language if possible, but the CONTENT must be in the target language.
- The final response MUST be 100% in the target language.

FINISH WITH THE EXACT PHRASE (In target language): "Do you wish to apply these tonal adjustments?"`,
            pt: "",
            es: "",
            fr: ""
        }
    };

    return prompts[taskType]?.[lang] || prompts[taskType]?.['en'] || "Analyze the script.";
}

export const sendIdeationMessage = async (
    history: Content[],
    newMessage: string,
    config: AppState['config'],
    scriptContext?: string
): Promise<string> => {
    return retryWithBackoff(async () => {
        const ai = await getAIInstance();
        const systemInstruction = getSystemInstruction(config);

        let contents = [...history];

        // Add script context if available as a user message context for this turn
        if (scriptContext) {
            contents.push({ role: 'user', parts: [{ text: `[Context - Current Script Content]: \n${scriptContext.substring(0, 20000)} ` }] });
        }

        // Add the new message to the conversation
        let finalMessage = newMessage;
        if (config.matureLanguage) {
            finalMessage += "\n\n[Permission Granted: Use mature language/profanity if the context (e.g. genre/characters) calls for it. Do not censor.]";
        }
        contents.push({ role: 'user', parts: [{ text: finalMessage }] });

        const modelName = await getBestModel(ai);
        const response = await ai.models.generateContent({
            model: modelName,
            contents: contents,
            config: {
                systemInstruction: systemInstruction,
                safetySettings: getSafetySettings(config)
            }
        });

        console.log("[AI] Response received successfully");
        return response.text || "";
    });
};

export const improvePrompt = async (prompt: string, config: AppState['config']): Promise<string> => {
    return retryWithBackoff(async () => {
        const ai = await getAIInstance();
        // Prompt improvement is usually fine in English as it's a meta-task, but let's localize if needed.
        // Keeping English for prompt engineering logic for now, but ensure output is prompt-ready.
        const modelName = await getBestModel(ai);
        const response = await ai.models.generateContent({
            model: modelName,
            contents: `Improve the following prompt for an AI screenwriter to be more specific, evocative, and technically precise: \n\n"${prompt}"\n\nReturn ONLY the improved prompt text.IMPORTANT: Maintain the original language of the prompt(e.g., if the prompt is in Portuguese, the improved version must also be in Portuguese).`,
            config: {
                safetySettings: getSafetySettings(config)
            }
        });
        return response.text?.trim() || prompt;
    });
};

export const generateDeepPillarAnalysis = async (
    scriptText: string,
    chatHistory: ChatMessage[],
    config: AppState['config']
): Promise<{ format: string, pillars: Record<string, string> }> => {
    return retryWithBackoff(async () => {
        const ai = await getAIInstance();
        const lang = config.language;

        // Construct chat context if available
        const chatContext = chatHistory.map(m => `${m.role.toUpperCase()}: ${m.content} `).join('\n');
        const truncatedScript = scriptText.substring(0, 30000);

        const prompt = `
    TASK:
    1. Classify the script format(Film, Animation, Documentary, or Advertisement).
            2. Based on the format, select the relevant pillars from the 27 Pillar System(Auteur.OS).
            3. Analyze the script / chat context and generate a concise but deep definition for EACH selected pillar.

            STRICT OUTPUT RULES:
    - Return ONLY JSON.
            - In the 'pillars' values, provide ONLY the analysis / definition text.
            - DO NOT include conversational filler(e.g., "Here is the definition", "As your consultant", "Como seu Consultor").
            - DO NOT ask questions.If information is missing, infer it creatively or state "To be defined".
            - Value text must be direct and objective.

            INPUT CONTEXT:
            ${chatContext ? `[CHAT MEMORY]\n${chatContext}\n` : ''}
    [SCRIPT CONTENT]
            ${truncatedScript || "No script yet. Use Chat Memory."}

            OUTPUT FORMAT(JSON ONLY):
    {
        "format": "Film" | "Animation" | "Documentary" | "Advertisement",
            "pillars": {
            "protagonist": "Analysis...",
                "objective": "Analysis...",
                    // ... include only relevant pillars for the format
                }
    }
            
            Use the keys from this list: ${PILLAR_LIBRARY.map(p => p.id).join(', ')}.
    Answer in ${lang.toUpperCase()}.
    `;

        const modelName = await getBestModel(ai);
        const response = await ai.models.generateContent({
            model: modelName,
            contents: prompt,
            config: {
                responseMimeType: "application/json",
                safetySettings: getSafetySettings(config)
            }
        });

        if (response.text) {
            return JSON.parse(response.text);
        }
        throw new Error("No response.");
    });
};

export const generatePillarSuggestion = async (
    chatHistory: ChatMessage[],
    pillarType: string,
    config: AppState['config']
): Promise<string> => {
    return retryWithBackoff(async () => {
        const ai = await getAIInstance();
        const lang = config.language;

        const history: Content[] = chatHistory.map(m => ({
            role: m.role === 'user' ? 'user' : 'model',
            parts: [{ text: m.content }]
        }));

        const instruction = `Suggest a concise but deep definition for the ${pillarType} of this story. Return ONLY the definition text, direct and objective, without conversational introductions or questions. Return the response EXCLUSIVELY in ${lang.toUpperCase()}.`;

        history.push({
            role: 'user',
            parts: [{ text: instruction }]
        });

        const modelName = await getBestModel(ai);
        const response = await ai.models.generateContent({
            model: modelName,
            contents: history,
            config: {
                safetySettings: getSafetySettings(config)
            }
        });

        const rawOutput = response.text || "";
        return cleanScriptWhitespace(rawOutput);
    });
};

export const generateScriptFromIdea = async (
    chatHistory: ChatMessage[],
    config: AppState['config'],
    isFirstDraft: boolean = false,
    formatId?: string,
    briefingAnswers?: Record<string, string>
): Promise<string> => {
    return retryWithBackoff(async () => {
        const ai = await getAIInstance();

        const lang = config.language || 'en';

        let systemInstruction = "";
        if (formatId) {
            const contextData = await getSystemContext(formatId, lang);
            systemInstruction = contextData.systemContext;
        } else {
            // IDEATION MODE: Clean slate (No pillars, No theory bloat) unless explicitly requested
            systemInstruction = getSystemInstruction(config, isFirstDraft, 'Film', false);
        }

        const history: Content[] = chatHistory.map(m => ({
            role: m.role === 'user' ? 'user' : 'model',
            parts: [{ text: m.content }]
        }));

        const langLabels: Record<string, string> = {
            pt: 'PORTUGUESE (Brazil)',
            es: 'SPANISH',
            fr: 'FRENCH',
            en: 'ENGLISH'
        };
        const targetLang = langLabels[lang] || 'ENGLISH';

        let finalPrompt = `
Generate the full screenplay (or the first major sequence) based on our development.

FORMATTING DIRECTIVE (STRICT):
You MUST strictly follow the rules defined in "[FORMATTING STANDARDS - STRICT]" in the System Instructions.

${GOLDEN_RULES}

LANGUAGE RULE: You MUST write the generated script EXCLUSIVELY in ${targetLang}.
        `;

        // INJECT BRIEFING ANSWERS IF AVAILABLE
        if (briefingAnswers && Object.keys(briefingAnswers).length > 0) {
            finalPrompt += `\n\n[USER BRIEFING ANSWERS]\nPlease incorporate the following core decisions into the script generation:\n`;
            for (const [key, val] of Object.entries(briefingAnswers)) {
                if (val && val.trim() !== '') {
                    finalPrompt += `- ${key}: ${val}\n`;
                }
            }
        }

        if (config.matureLanguage) {
            finalPrompt += `\n\n[MATURE CONTENT AUTHORIZED]: You are authorized to use explicit language, profanity, and adult themes if they fit the narrative tone.Do not censor the characters.`;
        }

        history.push({
            role: 'user',
            parts: [{ text: finalPrompt }]
        });

        const modelName = await getBestModel(ai);
        console.log(`[AI] Generating script with model: ${modelName} `);

        const response = await ai.models.generateContent({
            model: modelName,
            contents: history,
            config: {
                systemInstruction: systemInstruction,
                temperature: 1.0, // Internal rule: New script must be maximum creativity
                safetySettings: getSafetySettings(config)
            }
        });

        const rawOutput = response.text || "";
        return cleanScriptWhitespace(rawOutput);
    });
};

export const updateScriptFromChat = async (
    currentScript: string,
    chatHistory: ChatMessage[],
    config: AppState['config']
): Promise<string> => {
    return retryWithBackoff(async () => {
        const ai = await getAIInstance();
        // CHAT UPDATE MODE: Stay clean to prevent "Secret Bias"
        const systemInstruction = getSystemInstruction(config, false, 'Film', false);
        const lang = config.language || 'en';

        const lastUserRequest = chatHistory.filter(m => m.role === 'user').pop()?.content || "Improve the script.";

        const langLabels: Record<string, string> = {
            pt: 'PORTUGUESE (Brazil)',
            es: 'SPANISH',
            fr: 'FRENCH',
            en: 'ENGLISH'
        };
        const targetLang = langLabels[lang] || 'ENGLISH';

        const instruction = `
TASK:
Rewrite the script to incorporate the request below.

${GOLDEN_RULES}

LANGUAGE RULE: You MUST write the generated script EXCLUSIVELY in ${targetLang}.
        `;

        const prompt = `
            CURRENT SCRIPT:
            ${currentScript}

    REQUEST:
            ${lastUserRequest}

            ${instruction}
    `;

        const modelName = await getBestModel(ai);
        console.log(`[AI] Updating script with model: ${modelName} `);
        const response = await ai.models.generateContent({
            model: modelName,
            contents: [{ role: 'user', parts: [{ text: prompt }] }],
            config: {
                systemInstruction: systemInstruction,
                temperature: 1.0, // Internal rule: Chat updates must be maximum creativity
            }
        });

        return response.text || "";
    });
};

/**
 * Internal helper for applying a suggestion to a single chunk of script.
 */
async function updateScriptWithSuggestionSingle(
    currentScript: string,
    suggestion: string,
    config: AppState['config'],
    taskType?: string,
    temperature: number = 0.0
): Promise<string> {
    const apiKey = "dummy-secure-proxy";

    return executeWithResilience(async (signal) => {
        const ai = await getAIInstance();
        // ... (rest of lang logic)
        const lang = config.language || 'en';

        // 🛡️ INTERNAL RE-AUDITING PROTOCOL
        const isAuditTask = ['scriptaudit', 'script_review', 'plotaudit', 'continuity'].includes(taskType || "");

        const reAuditingProtocol = isAuditTask ? "" : `
    [CORRECTION WITH INTERNAL RE - AUDITING]
    1. BEFORE generating the correction, mentally simulate a re - audit of your suggested text.
        2. If your correction introduces a new technical error(ACT, FRM, DLG), DISCARD it.
        3. Keep the original text OR use a neutral, filmable alternative.
        4. "PASSED" THRESHOLD: If a sentence is already in the present tense, visual, and objective, mark it as "Verified" and do not change it.
        5. ZERO INTERNAL ERROR TERMS: Rewritten versions must NOT contain any terms from the internal error base(e.g., non - filmable adverbs, internal states).
        6. MINIMAL INVASION: Only alter the necessary part of the text to fix the identified issue.
        `;

        const taskInstructionsMap: Record<string, string> = {
            general: "Rewrite the script applying the suggestion.",
            continuity: "Rewrite the script to FIX the identified continuity discrepancies. Use ONLY the corrections described in the provided suggestions. Do NOT apply any suggestions that were not selected. Maintain the original script flow and only change what is strictly necessary to resolve the selected errors.",
            chararcs: "Rewrite the script to deepen the character arcs and emotional growth as suggested.",
            plotaudit: "Rewrite the script to apply ONLY the structural plot corrections suggested in the audit. DO NOT change the style, scenes, dialogues, or tone, except what is strictly necessary to fix the plot logic and structure. Maintain the original voice of the script while ensuring causal logic, motivation, and structural balance.",
            scriptaudit: `BLOCK - AWARE SUBSTITUTION MODE. For each item in APPLY_AUDIT_FIXES:
                1. Locate the "original" snippet in the script.
                2. REPLACE IT FULLY with the "suggestion".
                3. If the "suggestion" splits a block into Dialogue and Action (Law of the Break), YOU MUST PRESERVE THIS SPLIT. Never re-merge the action back into the dialogue.
                4. INTEGRITY DIRECTIVE: Preserve all other parts of the original script exactly as they are. FORBIDDEN to summarize or omit lines.
                5. FORMATTING: Character names in ALL CAPS. Headings in ALL CAPS. Double newlines (\\n\\n) between different elements.`,
            script_review: `BLOCK-AWARE SUBSTITUTION MODE.
                1. Locate the "original" snippet uniquely in the script.
                2. REPLACE IT FULLY with the "suggestion".
                3. HEALING SYNC: If the "suggestion" includes more blocks than the original (e.g., heals a dialogue with vocalizations AND adds an action line), you MUST insert ALL of them with proper \n\n separators.
                4. Do NOT change anything else.
                5. WHITESPACE: Characters are followed directly by Dialogue. Dialogue is followed by \n\n before Action.`,
            plottwist: "Rewrite the script incorporating the suggested Plot Twist. This is a deep structural change: you MUST alter the ending, climax, or crucial revelations so that the twist is organically integrated. Recontextualize whatever is necessary for the story's new meaning to make sense."
        };

        const taskInstruction = (taskType && taskInstructionsMap[taskType]) || taskInstructionsMap['general'];

        const prompt = `
            # ACTOR: SCRIPT REWRITER(BLOCK MODE)
            
            Original Script:
            ${currentScript}

    APPLY_AUDIT_FIXES(The following JSON contains snippets to replace):
            ${suggestion}

    Task: ${taskInstruction}
            
            LANGUAGE PRESERVATION (CRITICAL): You MUST preserve the ORIGINAL LANGUAGE of the script content. Do NOT translate the script to any other language. If the script is in English, keep it in English. If it is in Portuguese, keep it in Portuguese. The corrections must be in the SAME LANGUAGE as the original script.
            
            CRITICAL OUTPUT RULE: Return ONLY the full revised script text.
            - Do NOT include "Revised Script:" or any other labels.
            - Do NOT include markdown code blocks(\`\`\`).
            - Do NOT include explanations.
            - Just the raw Fountain-formatted script content ready to be saved.
            
            # CORE INTEGRITY RULES:
            - **STRICT STRUCTURAL SYNC**: If a suggestion in APPLY_AUDIT_FIXES includes blank lines or separates a block into multiple elements (e.g., Dialogue -> Action), YOU MUST RESPECT THIS NEW STRUCTURE. Do NOT merge them back.
            - **PRESERVATION**: Every scene, every segment, and every line NOT mentioned in the suggestions MUST remain identical.
            - **ZERO SUMMARIZATION**: Do NOT omit any part of the script.
            - **CLEAN FOUNTAIN**: ONLY Character names (above dialogue) and Scene Headings (INT/EXT) are ALL CAPS. Action lines and Dialogue MUST be in standard sentence case. NEVER convert a regular action paragraph into ALL CAPS.
            - **ANTI-DUPLICATION (CRITICAL)**: When replacing an original snippet with a suggestion, YOU MUST OVERWRITE IT COMPLETELY. NEVER leave the original text intact and paste the suggestion below it. If the suggestion is to turn a prose paragraph into a bulleted list, REPLACE the prose with the list. Do NOT output both.
            `;

        const systemInstructionText = `
Role: AuteurDraft Script Engine.
Task: Reform script applying requested suggestions.
STRICT OUTPUT RULES:
- You must return ONLY the full revised script in Fountain format.
- DO NOT include conversational fillers ("Here is the script", "Revised version:").
- DO NOT use markers like "Revised Script:" or delimit the output.
- The output should start directly with the Scene Heading (Slugline) or the first line of the script.
- Maintain strict formatting (Action, Character, Dialogue).
- LANGUAGE PRESERVATION: You MUST keep the script in its ORIGINAL LANGUAGE. Detect the language from the script content and DO NOT translate it. If the script is in English, output in English. If in Portuguese, output in Portuguese.
- NO HTML TAGS: Never use <center> or any HTML tags.

[FORMATTING STANDARDS - STRICT]
${getFormattingRules('en')}

[GOLD STANDARD CORRECTION EXAMPLES]
${getGoldStandard('en')}
`;

        const modelName = await getBestProModel(ai);

        const finalTemperature = temperature !== undefined ? temperature :
            (['scriptaudit', 'script_review', 'plotaudit'].includes(taskType || '') ? 0.0 : 1.0);

        const res = await ai.models.generateContent({
            model: modelName,
            contents: [{ role: 'user', parts: [{ text: prompt }] }],
            config: {
                systemInstruction: systemInstructionText,
                temperature: finalTemperature
            }
        });

        const rawOutput = res.text || "";
        return cleanScriptWhitespace(rawOutput);
    }, { maxRetries: 3, timeoutMs: 30000 });
}
;

/**
 * MAIN ENTRY POINT: applies AI suggestions to the script.
 * For large scripts, it automatically chunks the request to avoid token limits.
 */
export const updateScriptWithSuggestion = async (
    currentScript: string,
    suggestion: string,
    config: AppState['config'],
    taskType?: string,
    temperature?: number
): Promise<string> => {

    const cleanSuggestion = suggestion.trim();

    if (cleanSuggestion.startsWith('APPLY_PLOT_AUDIT_FIXES:')) {
        const apiKey = "dummy-secure-proxy";

        return executeWithResilience(async (signal) => {
            console.log("[Script Update] Applying PLOT AUDIT fixes via targeted scene replacement...");

            const jsonPart = cleanSuggestion.split('APPLY_PLOT_AUDIT_FIXES:\n')[1]?.trim() || '';

            const prompt = `
                [TASK: STRUCTURAL PLOT REWRITE]
                You are a Senior Script Doctor.
                The following script has structural plot holes:
                ${jsonPart}

                Your task is to rewrite ONLY the specific scenes that need changing to fix these plot holes.
                Do not rewrite the entire script.

                [SCRIPT TEXT]
                ${currentScript}

                [OUTPUT FORMAT]
                You must return a strictly valid JSON array containing the rewritten scenes.
                Do not wrap in markdown \`\`\`json. Return pure JSON.
                Format:
                [
                    {
                        "original_scene_heading": "The exact original scene heading (EXT. LOCATION - DAY)",
                        "new_scene_content": "The completely rewritten scene, starting with the scene heading and ending before the next scene."
                    }
                ]
            `;

            const ai = await getAIInstance();
            const modelName = await getBestProModel(ai);

            const res = await ai.models.generateContent({
                model: modelName,
                contents: [{ role: 'user', parts: [{ text: prompt }] }],
                config: {
                    systemInstruction: getRole(config.language || 'en', config.scriptFormat || 'Film'),
                    temperature: 0.3,
                    responseMimeType: "application/json"
                }
            });

            const text = res.text || "[]";

            let parsedScenes = [];
            try {
                const cleanText = text.replace(/^```json/i, '').replace(/^```/i, '').replace(/```$/i, '').trim();
                parsedScenes = JSON.parse(cleanText);
            } catch (e) {
                console.error("[Script Update] Failed to parse PLOT AUDIT JSON response", text);
                throw new Error("Failed to parse structural plot fixes from AI response.");
            }

            let modifiedScript = currentScript;

            for (const scene of parsedScenes) {
                if (!scene.original_scene_heading || !scene.new_scene_content) continue;

                const heading = scene.original_scene_heading.trim().toUpperCase();
                const newContent = scene.new_scene_content.trim();

                const startIndex = modifiedScript.toUpperCase().indexOf(heading);
                if (startIndex === -1) continue;

                const nextSceneRegex = /\n(?:INT\.|EXT\.|INT\/EXT|EXT\/INT|I\/E)(?=\s)/ig;
                nextSceneRegex.lastIndex = startIndex + heading.length;
                const nextMatch = nextSceneRegex.exec(modifiedScript);

                const endIndex = nextMatch ? nextMatch.index : modifiedScript.length;

                const before = modifiedScript.substring(0, startIndex);
                const after = modifiedScript.substring(endIndex);

                modifiedScript = before + newContent + '\n\n' + after.trimStart();
            }

            return cleanScriptWhitespace(modifiedScript, config.scriptMode, config.language, false);
        }, { maxRetries: 3, timeoutMs: 30000 });
    }

    // 1. Extrair os patches JSON da sugestão da IA de forma blindada
    let issues: any[] = [];
    let isJsonSuggestion = false;
    try {
        let cleanSuggestion = suggestion.trim();
        // 🛡️ ANTI-MARKDOWN SHIELD 🛡️
        // Captura apenas o que está entre colchetes, ignorando crases e blocos ```json
        const jsonMatch = cleanSuggestion.match(/\[[\s\S]*\]/);

        if (jsonMatch) {
            issues = JSON.parse(jsonMatch[0]);
            isJsonSuggestion = true;
        } else if (cleanSuggestion.includes('APPLY_AUDIT_FIXES')) {
            const jsonPart = cleanSuggestion.split('APPLY_AUDIT_FIXES:')[1]?.trim() || cleanSuggestion;
            issues = JSON.parse(jsonPart);
            isJsonSuggestion = true;
        }
    } catch (e) {
        console.warn("[updateScriptWithSuggestion] Suggestion failed to parse. Fallback triggered.", e);
    }

    // 🔥 2. PATCH DETERMINÍSTICO BLINDADO (SEM FALLBACK) 🔥
    if (isJsonSuggestion && Array.isArray(issues) && issues.length > 0) {
        let modifiedScript = currentScript;
        let appliedCount = 0;

        // ORDENAÇÃO DE SEGURANÇA: Aplica os patches MAIORES primeiro.
        // Isso impede duplicação caso a IA envie dois patches sobrepostos para o mesmo bloco de texto.
        issues.sort((a, b) => (b.original?.length || 0) - (a.original?.length || 0));

        for (const issue of issues) {
            if (!issue.original || typeof issue.suggestion !== 'string') continue;

            const orig = issue.original.trim();
            const sugg = issue.suggestion.trim();

            if (!orig) continue;

            // 🛡️ FILTRO ANTI-DUPLICAÇÃO (FAILSAFE) 🛡️
            // Se a sugestão contém o texto original intacto E é muito maior (eco do LLM), bloqueie o patch.
            const cleanOrigForCheck = orig.replace(/\s+/g, ' ');
            const cleanSuggForCheck = sugg.replace(/\s+/g, ' ');
            if (cleanSuggForCheck.includes(cleanOrigForCheck) && cleanSuggForCheck.length > cleanOrigForCheck.length * 1.3 && cleanOrigForCheck.length > 20) {
                console.warn(`[Script Update] BLOCKED Duplication Hallucination: AI appended text instead of replacing.`);
                continue; // Descarta a correção corrompida
            }

            // Busca flexível SEM a flag 'g' (global). 
            // Substitui apenas a primeira instância exata, impedindo efeito cascata em textos repetidos (bullets).
            const escapeRegExp = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const flexibleSearch = new RegExp(escapeRegExp(orig).replace(/\s+/g, '\\s+'));

            // Verifica se o texto ainda existe no script (pode ter sido engolido por um patch maior anterior)
            if (flexibleSearch.test(modifiedScript)) {
                modifiedScript = modifiedScript.replace(flexibleSearch, `[[AI_SUGG_START]]\n${sugg}\n[[AI_SUGG_END]]`);
                appliedCount++;
            } else if (modifiedScript.includes(orig)) {
                modifiedScript = modifiedScript.replace(orig, `[[AI_SUGG_START]]\n${sugg}\n[[AI_SUGG_END]]`);
                appliedCount++;
            }
        }

        console.log(`[Script Update] Deterministic patch applied ${appliedCount}/${issues.length} fixes. FORCING RETURN.`);
        // RETORNO ABSOLUTO: Se for um JSON de auditoria, jamais repasse para o LLM reescrever.
        return cleanScriptWhitespace(modifiedScript, config.scriptMode, config.language, false);
    }

    // 3. FALLBACK PARA O LLM
    const CHUNK_THRESHOLD = 20000;

    if (!currentScript || currentScript.length <= CHUNK_THRESHOLD) {
        return updateScriptWithSuggestionSingle(currentScript, suggestion, config, taskType, temperature || 0.0);
    }

    console.log(`[Script Update] Script is large (${currentScript.length} chars). Chunking application...`);

    const chunks: string[] = [];
    let remaining = currentScript;
    while (remaining.length > 0) {
        if (remaining.length <= CHUNK_THRESHOLD) {
            chunks.push(remaining);
            break;
        }

        let splitIdx = remaining.lastIndexOf('\n\n', CHUNK_THRESHOLD);
        if (splitIdx === -1) splitIdx = remaining.lastIndexOf('\n', CHUNK_THRESHOLD);
        if (splitIdx === -1) splitIdx = CHUNK_THRESHOLD;

        chunks.push(remaining.substring(0, splitIdx));
        remaining = remaining.substring(splitIdx).trim();
    }

    const results: string[] = [];
    for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        let chunkSuggestion = suggestion;
        let shouldProcess = true;

        if (isJsonSuggestion && issues.length > 0) {
            const chunkIssues = issues.filter(issue => issue.original && chunk.includes(issue.original.trim()));

            if (chunkIssues.length === 0) {
                results.push(chunk);
                shouldProcess = false;
            } else {
                chunkSuggestion = `APPLY_AUDIT_FIXES:\n${JSON.stringify(chunkIssues)}`;
            }
        }

        if (shouldProcess) {
            console.log(`[Script Update] Processing chunk ${i + 1}/${chunks.length} (${chunk.length} chars)...`);
            const processed = await updateScriptWithSuggestionSingle(chunk, chunkSuggestion, config, taskType, temperature || 0.0);
            results.push(processed);
        }
    }

    return results.filter(Boolean).join('\n\n');
};

export const rewriteScriptBlock = async (
    fullScript: string,
    blockContent: string,
    config: AppState['config']
): Promise<string> => {
    return retryWithBackoff(async () => {
        const ai = await getAIInstance();
        const lang = config.language || 'en';
        const modelName = await getBestModel(ai);

        // ATOMIC PROMPT - Isolated from global narrative theories
        const prompt = `
            [TASK: REWRITE SCRIPT BLOCK]
            You are a Senior Script Doctor. Rewrite the snippet below to be more impactful, concise, and professional.
            
            [OBJECTIVE]
            Maintain the EXACT same element type (Action remains Action, Dialogue remains Dialogue).
            
            [FORMATTING RULES]
            ${getFormattingRules(lang)}
            
            [BLOCK TO REWRITE]
            "${blockContent}"
            
            [OUTPUT DIRECTIVE]
            Return ONLY the rewritten text in ${(LANGUAGE_MAP[lang] || 'English').toUpperCase()}. No metadata. No quotes.
        `;

        const response = await ai.models.generateContent({
            model: modelName,
            contents: [{ role: 'user', parts: [{ text: prompt }] }],
            config: {
                systemInstruction: getRole(lang, config.scriptFormat || 'Film'),
                temperature: 0.4
            }
        });

        return cleanScriptWhitespace(response.text || blockContent);
    });
};

export const improveDialogue = async (
    fullScript: string,
    dialogueContent: string,
    config: AppConfig
): Promise<string> => {
    return retryWithBackoff(async () => {
        const ai = await getAIInstance();
        const lang = config.language || 'en';
        const modelName = await getBestProModel(ai); // Dialogue needs voice sensitivity

        const langLabels: Record<string, string> = {
            pt: 'PORTUGUESE (Brazil)',
            es: 'SPANISH',
            fr: 'FRENCH',
            en: 'ENGLISH'
        };
        const targetLang = langLabels[lang] || 'ENGLISH';

        // ATOMIC PROMPT - Isolated Context
        const prompt = `
            [TASK: DIALOGUE POLISHING]
            Rewrite the dialogue below to make it more natural, subtextual, and aligned to a professional cinematic voice.

            [RULES OF GOOD DIALOGUE]
            - Eliminate "on the nose" exposition.
            - Focus on subtext: characters should not say exactly what they feel.
            - Maintain rhythmic brevity.
            - Ensure it remains formatted correctly as dialogue.

            [TECHNICAL CONSTRAINTS]
            ${getFormattingRules(lang)}

            [DIALOGUE TO IMPROVE]
            "${dialogueContent}"

            [OUTPUT]
            Return ONLY the improved dialogue text in ${targetLang}. No quotes. No conversational filler.
        `;

        const response = await ai.models.generateContent({
            model: modelName,
            contents: [{ role: 'user', parts: [{ text: prompt }] }],
            config: {
                systemInstruction: getRole(lang, config.scriptFormat || 'Film'),
                temperature: 0.7
            }
        });

        return cleanScriptWhitespace(response.text || dialogueContent);
    });
};

/**
 * ATOMIC TOOL: Plant & Payoff Injection
 */
export const applyPlantAndPayoff = async (
    scriptText: string,
    config: AppConfig
): Promise<string> => {
    return retryWithBackoff(async () => {
        const ai = await getAIInstance();
        const lang = config.language || 'en';
        const modelName = await getBestProModel(ai);

        const prompt = `
            [TASK: PLANT & PAYOFF INJECTION]
            You are a Script Doctor specializing in structural setup and payoff.
            Analyze the scene below and insert subtle "Plant" (setup) or "Payoff" (resolution) elements.

            [METHODOLOGY]
            ${PLANT_AND_PAYOFF_KNOWLEDGE}

            [STRICT FORMATTING]
            ${getFormattingRules(lang)}

            [CONSTRAINTS]
            1. DO NOT change the main arc of the scene.
            2. DO NOT add new characters.
            3. Add ONLY the physical detail or line of dialogue needed for the setup/payoff.
            
            [ORIGINAL TEXT]
            ${scriptText}

            [OUTPUT]
            Return ONLY the rewritten text in Fountain format.
        `;

        const response = await ai.models.generateContent({
            model: modelName,
            contents: [{ role: 'user', parts: [{ text: prompt }] }],
            config: {
                systemInstruction: getRole(lang, config.scriptFormat || 'Film'),
                temperature: 0.4
            }
        });

        return cleanScriptWhitespace(response.text || scriptText);
    });
};

export const generateScriptMetadata = async (
    scriptText: string,
    config: AppState['config']
): Promise<string> => {
    return retryWithBackoff(async () => {
        const ai = await getAIInstance();
        // Localize metadata keys based on language so the parser can read them
        const lang = config.language;
        let promptKeys = "";

        if (lang === 'pt') {
            promptKeys = `
            ### CLASSIFICAÇÃO/GÊNERO
            ### RITMO
            ### ANÁLISE SOB A ÓTICA "PLANT AND PAYOFF"
            ### VERIFICAÇÃO DOS PILARES DA HISTÓRIA
            ### PROTAGONISTA(S)
            ### OBJETIVOS (PROTAGONISTA E ANTAGONISTA)
            ### CONFLITOS
            ### CENÁRIO(S) / ERA(S)
            ### PLOT TWIST
            ### FUROS DE ROTEIRO / LÓGICA
            `;
        } else if (lang === 'es') {
            promptKeys = `
            ### CLASIFICACIÓN/GÉNERO
            ### RITMO
            ### ANÁLISIS "PLANT AND PAYOFF"
            ### VERIFICACIÓN DE PILARES DE LA HISTORIA
            ### PROTAGONISTA(S)
            ### OBJETIVOS (PROTAGONISTA Y ANTAGONISTA)
            ### CONFLICTOS
            ### ESCENARIO(S) / ERA(S)
            ### PLOT TWIST (GIRO)
            ### AGUJEROS DE GUION / LÓGICA
            `;
        } else if (lang === 'fr') {
            promptKeys = `
            ### CLASSIFICATION/GENRE
            ### RYTHME
            ### ANALYSE "PLANT AND PAYOFF"
            ### VÉRIFICATION DES PILIERS DE L'HISTOIRE
            ### PROTAGONISTE(S)
            ### OBJECTIFS (PROTAGONISTE ET ANTAGONISTE)
            ### CONFLITS
            ### DÉCOR(S) / ÉPOQUE(S)
            ### PLOT TWIST
            ### INCOHÉRENCES / LOGIQUE
            `;
        } else {
            promptKeys = `
            ### CLASSIFICATION/GENRE
            ### PACING/RHYTHM
            ### "PLANT AND PAYOFF" ANALYSIS
            ### STORY PILLARS VERIFICATION
            ### PROTAGONIST(S)
            ### OBJECTIVES (PROTAGONIST & ANTAGONIST)
            ### CONFLICTS
            ### SETTING/ERA
            ### PLOT TWIST
            ### PLOT HOLES / LOGIC GAPS
            `;
        }

        const langLabels: Record<string, string> = {
            pt: 'PORTUGUESE (Brazil)',
            es: 'SPANISH',
            fr: 'FRENCH',
            en: 'ENGLISH'
        };
        const targetLang = langLabels[lang || 'en'] || 'ENGLISH';

        const prompt = `
            Act as a professional Script Doctor (Auteur.OS).
            Analyze the following script text thoroughly.
            
            Use the following context definitions for your analysis:
            
            [PLANT AND PAYOFF THEORY]
            ${PLANT_AND_PAYOFF_KNOWLEDGE}

            [PLOT TWIST THEORY]
            ${PLOT_TWIST_KNOWLEDGE}
            
            [AUTEUR PILLARS]
            ${AUTEUR_PILLARS_KNOWLEDGE_EN}

            Provide a detailed analysis metadata report using STRICTLY these headers.
            The content and analysis MUST be written EXCLUSIVELY in ${targetLang}.

            REQUIRED HEADERS:
            ${promptKeys}

            Instructions for analysis sections:
            - Pacing/Rhythm: Analyze the pacing of scenes and dialogue.
            - Plant and Payoff: Identify setup and payoffs.
            - Pillars Check: Briefly check against the 27 pillars if applicable.
            - Logic/Plot Holes: Explicitly state "None detected" (in ${targetLang}) if the logic is sound, otherwise list them.

            Script to Analyze:
            ${scriptText.substring(0, 80000)}
        `;
        const modelName = await getBestModel(ai);
        const response = await ai.models.generateContent({
            model: modelName,
            contents: prompt,
        });
        return response.text || "";
    });
};

export const translateScript = async (
    scriptText: string,
    targetLang: string,
    config: AppState['config'],
    onProgress?: (batch: number, total: number) => void
): Promise<string> => {
    return retryWithBackoff(async () => {
        const ai = await getAIInstance();
        const modelName = await getBestModel(ai);

        // Split by SCENE HEADING (approximate)
        // We use typical markers: INT. or EXT. etc.
        const scenes = scriptText.split(/(?=^INT\.|^EXT\.|^I\/E|^INT |^EXT |^CENA|^SCENE|^EST\.)/m);

        // Group scenes into batches to reduce API calls while staying within output limits
        // ~8 scenes per batch is usually safe for screenplay format (~2000-3000 output tokens)
        const BATCH_SIZE = 8;
        const batches: string[] = [];
        for (let i = 0; i < scenes.length; i += BATCH_SIZE) {
            batches.push(scenes.slice(i, i + BATCH_SIZE).join('\n'));
        }

        let translatedScript = "";

        // Translate batches sequentially to maintain consistency and allow for progress feedback
        for (let i = 0; i < batches.length; i++) {
            if (onProgress) onProgress(i + 1, batches.length);

            const prompt = `
                TASK: Translate the following screenplay segment into ${targetLang}. 
                
                STRICT RULES:
                1. PRESERVE SCREENPLAY FORMAT (Fountain).
                2. DO NOT SUMMARIZE. Translate every line of dialogue and action exactly.
                3. DO NOT ADD INTRODUCTIONS (e.g. "Here is the translation"). Return ONLY the translated screenplay text.
                4. Maintain consistent character names and tone.
                
                SEGMENT (${i + 1}/${batches.length}):
                ${batches[i]}
            `;

            const response = await ai.models.generateContent({
                model: modelName,
                contents: prompt,
            });

            translatedScript += (response.text || "") + "\n\n";
        }

        return translatedScript.trim();
    });
};

export const extractCharactersFromText = async (
    text: string,
    config: AppConfig,
    targetNames?: string[]
): Promise<CharacterProfile[]> => {
    return retryWithBackoff(async () => {
        const ai = await getAIInstance();
        const lang = config.language;
        const langLabel = lang === 'pt' ? 'Portuguese (Português do Brasil)'
            : lang === 'es' ? 'Spanish (Español)'
                : lang === 'fr' ? 'French (Français)'
                    : 'English';

        const missionDesc = 'You are a dramatic analytical engine. Your task is to read the provided text (Film/TV/Doc/Ad/Animation script), identify relevant entities, and structure their profiles for a production database.';

        const rulesDesc = `
1. THE "ACTION OVER DIALOGUE" RULE (CRITICAL)
   - In fiction, characters lie. If a character says "I'm brave" but flees, record "Coward/Insecure". Actions are truth; dialogues are strategy.

2. THE "ANTI-HALLUCINATION" PROTOCOL
   - Do not invent data. If text doesn't mention age, use "Not Identified" in ${langLabel}.
   - Use null if fields have no evidence.

3. RELEVANCE FILTER
   - Ignore generic background characters unless they perform plot-changing actions.
`;

        const systemInstruction = `
# SYSTEM CONFIGURATION
Role: AUTEUR.OS INTELLIGENCE
Output Enforcement: Strict JSON Array ONLY. No preamble. No markdown blocks.

# MISSION
${missionDesc}

# CORE OPERATING RULES
${rulesDesc}

STRICT LANGUAGE RULE:
- All values in the JSON (except keys) MUST be in ${langLabel}.
- Do NOT output English if ${langLabel} is not English.

# OUTPUT JSON SCHEMA
Respond ONLY with a JSON object array following this exact structure (Values MUST BE IN ${langLabel.toUpperCase()}):

[
  {
    "name": "NAME (UPPERCASE)",
    "role": "Protagonista | Antagonista | Coadjuvante | Figurante | Entrevistado | Narrador | Mascote",
    "data": {
      "identity": {
        "full_name": "...",
        "age_range": "...",
        "profession_or_role": "...",
        "social_class": "...",
        "era_setting": "...",
        "current_location": "...",
        "is_real_person": boolean,
        "is_inferred": boolean
      },
      "psychology": {
        "archetype": "...",
        "personality_traits": ["...", "..."],
        "moral_code_or_stance": "...",
        "core_fears_or_pains": "...",
        "primary_goal": "..."
      },
      "expression": {
        "speech_style": "...",
        "conversation_topics": ["...", "..."],
        "sense_of_humor": "...",
        "visual_description": "..."
      }
    }
  }
]
`;

        const inputContextLabel = lang === 'pt' ? 'Abaixo está o conteúdo do roteiro (ou fragmento) para análise imediata. Execute as regras do System Instruction sobre este texto:'
            : lang === 'es' ? 'A continuación se muestra el contenido del guion (o fragmento) para su análisis inmediato. Ejecute las reglas de System Instruction sobre este texto:'
                : lang === 'fr' ? 'Vous trouverez ci-dessous le contenu du scénario (ou fragment) pour analyse immédiate. Exécutez les règles de la System Instruction sur ce texte :'
                    : 'Below is the script content (or fragment) for immediate analysis. Execute the System Instruction rules on this text:';

        const priorityLabel = lang === 'pt' ? 'PRIORIDADE DE EXTRAÇÃO (FOCO)'
            : lang === 'es' ? 'PRIORIDAD DE EXTRACCIÓN (ENFOQUE)'
                : lang === 'fr' ? 'PRIORITÉ D\'EXTRACTION (FOCUS)'
                    : 'EXTRACTION PRIORITY (FOCUS)';

        const priorityText = lang === 'pt' ? 'O usuário identificou manualmente os seguintes personagens no roteiro, mas eles estão sem perfil. Você DEVE obrigatoriamente tentar localizar evidências para eles no texto e gerar seus perfis:'
            : lang === 'es' ? 'El usuario ha identificado manualmente a los siguientes personajes, pero no tienen perfil. DEBE intentar localizar evidencias para ellos y generar sus perfiles:'
                : lang === 'fr' ? 'L\'utilisateur a identifié manuellement les personnages suivants, mais ils n\'ont pas de profil. Vous DEVEZ essayer de localiser des preuves les concernant et générer leurs profils :'
                    : 'The user manually identified the following characters, but they have no profile. You MUST try to locate evidence for them and generate their profiles:';

        const prompt = `
# INPUT CONTEXT
${inputContextLabel}

${targetNames && targetNames.length > 0 ? `
# ${priorityLabel}
${priorityText}
${targetNames.join(', ')}
` : ''}

--- START OF SCRIPT ---
${text}
--- END OF SCRIPT ---
        `;

        const modelName = await getBestModel(ai);
        const response = await ai.models.generateContent({
            model: modelName,
            contents: prompt,
            config: {
                systemInstruction: systemInstruction,
                responseMimeType: "application/json",
                safetySettings: getSafetySettings(config)
            }
        });

        const resultText = response.text || "[]";
        try {
            return JSON.parse(resultText) as CharacterProfile[];
        } catch (e) {
            console.error("Failed to parse AI character extraction result:", e);
            return [];
        }
    });
};

export const extractCharacterProfileFromName = async (
    name: string,
    scriptText: string,
    config: AppConfig
): Promise<Partial<CharacterProfile> | null> => {
    return retryWithBackoff(async () => {
        const ai = await getAIInstance();
        const lang = config.language;
        const langLabel = lang === 'pt' ? 'Portuguese (Português do Brasil)'
            : lang === 'es' ? 'Spanish (Español)'
                : lang === 'fr' ? 'French (Français)'
                    : 'English';

        const taskDesc = `Read the script and extract the profile ONLY for the character: "${name.toUpperCase()}".`;

        const rulesDesc = `
1. THE "ACTION OVER DIALOGUE" RULE (CRITICAL)
   - In fiction, characters lie. If a character says "I'm brave" but flees, record "Coward/Insecure".
2. THE "ANTI-HALLUCINATION" PROTOCOL
   - Do not invent data. Use null if no evidence.
   - If character is not found, return null.
`;

        const systemInstruction = `
ROLE: Character Profiler
TASK: ${taskDesc}

# CORE OPERATING RULES
${rulesDesc}

STRICT LANGUAGE RULE:
- All values in the JSON (except keys) MUST be in ${langLabel}.
- Do NOT output English if ${langLabel} is not English.

# OUTPUT JSON SCHEMA
Return a STRICT JSON object FOLLOWING THIS STRUCTURE (Values MUST BE IN ${langLabel.toUpperCase()}):
{
  "name": "${name.toUpperCase()}",
  "role": "Protagonista | Antagonista | Coadjuvante | Figurante | Entrevistado | Narrador | Mascote",
  "data": {
    "identity": {
      "full_name": "string",
      "age_range": "string",
      "profession_or_role": "string",
      "social_class": "string",
      "era_setting": "string",
      "current_location": "string",
      "is_real_person": boolean,
      "is_inferred": boolean
    },
    "psychology": {
      "archetype": "string",
      "personality_traits": ["string"],
      "moral_code_or_stance": "string | null",
      "core_fears_or_pains": "string | null",
      "primary_goal": "string"
    },
    "expression": {
      "speech_style": "string",
      "conversation_topics": ["string"],
      "sense_of_humor": "string",
      "visual_description": "string"
    }
  }
}

If the character is not found or there is not enough data, return null.
`;

        const truncatedScript = scriptText.substring(0, 50000); // Send up to ~50 scenes/content
        const promptIntro = `Extract the profile for character "${name.toUpperCase()}" based on the following script:`;

        const prompt = `${promptIntro}\n\n--- START OF SCRIPT ---\n${truncatedScript}\n--- END OF SCRIPT ---`;

        const modelName = await getBestModel(ai);
        const response = await ai.models.generateContent({
            model: modelName,
            contents: prompt,
            config: {
                systemInstruction: systemInstruction,
                responseMimeType: "application/json",
                safetySettings: getSafetySettings(config)
            }
        });

        if (response.text) {
            try {
                const result = JSON.parse(response.text);
                return result;
            } catch (e) {
                console.error("Failed to parse AI response for character extraction", e);
                return null;
            }
        }
        return null;
    });
};


export const translateText = async (
    text: string,
    targetLang: string
): Promise<string> => {
    return retryWithBackoff(async () => {
        const ai = await getAIInstance();
        const prompt = `Translate the following text into ${targetLang}:\n\n${text}`;
        const modelName = await getBestModel(ai);
        const response = await ai.models.generateContent({
            model: modelName,
            contents: prompt,
        });
        return response.text || "";
    });
};

export const detectLanguage = async (
    text: string
): Promise<Language> => {
    return retryWithBackoff(async () => {
        const ai = await getAIInstance();
        const prompt = `Identify the language of the following text. 
        Options: 'en', 'pt', 'es', 'fr'. 
        Text snippet: "${text.substring(0, 5000)}"
        Return ONLY the language code (e.g., 'en'). If unsure, return 'en'.`;

        const modelName = await getBestModel(ai);
        const response = await ai.models.generateContent({
            model: modelName,
            contents: prompt,
        });
        const lang = response.text?.trim().toLowerCase();
        if (['en', 'pt', 'es', 'fr'].includes(lang as any)) {
            return lang as Language;
        }
        return 'en'; // Default to English if detection fails
    });
};

/**
 * FORMAT SCRIPT (The Ingestion Engine)
 * Takes raw, messy text and rewrites it into clean Master Scene (Fountain) format.
 * For large scripts, it automatically chunks the request to avoid token limits.
 */
export const formatScript = async (
    rawText: string,
    config: AppState['config'],
    creativity: number = 0.2
): Promise<string> => {
    if (!rawText || rawText.trim().length === 0) return "";

    // Chunk size: ~15,000 chars is safe for output limits and keeps context coherent
    const CHUNK_SIZE = 15000;

    if (rawText.length <= CHUNK_SIZE) {
        return await formatScriptSingle(rawText, config, creativity);
    }

    // Large Script Logic (Parallel Batching)
    console.log(`[Import] Script is large (${rawText.length} chars). Using parallel batching...`);

    // Split text by approximate size, trying to respect double newlines (scene boundaries)
    const chunks: string[] = [];
    let remaining = rawText;
    while (remaining.length > 0) {
        if (remaining.length <= CHUNK_SIZE) {
            chunks.push(remaining);
            break;
        }

        let splitIdx = remaining.lastIndexOf('\n\n', CHUNK_SIZE);
        if (splitIdx === -1) splitIdx = remaining.lastIndexOf('\n', CHUNK_SIZE);
        if (splitIdx === -1) splitIdx = CHUNK_SIZE;

        chunks.push(remaining.substring(0, splitIdx));
        remaining = remaining.substring(splitIdx).trim();
    }


    // Process all chunks in parallel with Context Overlap (1000 chars) for continuity
    const results = await Promise.all(
        chunks.map((chunk, i) => {
            const contextPrefix = i > 0 ? chunks[i - 1].slice(-1000) : "";
            return formatScriptSingle(chunk, config, creativity, i === 0, i > 0, contextPrefix);
        })
    );

    return results.filter(Boolean).join('\n\n');
};

/**
 * Internal helper for formatting a single chunk of script.
 */
async function formatScriptSingle(
    rawText: string,
    config: AppState['config'],
    creativity: number,
    isFirstChunk: boolean = true,
    isContinuation: boolean = false,
    contextPrefix: string = ""
): Promise<string> {
    return retryWithBackoff(async () => {
        const ai = await getAIInstance();
        const modelName = await getBestModel(ai);

        let creativityGuideline = "";
        if (creativity <= 0.1) {
            creativityGuideline = "STRICT LITERAL: You are a faithful transcriber. Copy the input text EXACTLY as it is. ONLY normalize formatting (ALL CAPS for names/slugs, verify INT./EXT., spacing). DO NOT REWRITE ACTION. DO NOT REMOVE ADJECTIVES. DO NOT ADD DIALOGUE. DO NOT SUMMARIZE. The output must have the SAME amount of content as the input.";
        } else if (creativity <= 0.3) {
            creativityGuideline = "LITERAL: Faithfully copy the pasted text. Perform formatting normalization and minimal grammar corrections. DO NOT add scenes or narrative inference.";
        } else if (creativity <= 0.6) {
            creativityGuideline = "BALANCED: Preserve original structure. You may improve dramatic flow and slightly expand implicit actions. DO NOT create new narrative arcs.";
        } else {
            creativityGuideline = "CREATIVE: Use text as inspiration. You may restructure scenes, create dialogue, and introduce original narrative elements.";
        }

        const langLabel = config.language === 'pt' ? 'Portuguese (BR)'
            : config.language === 'es' ? 'Spanish'
                : config.language === 'fr' ? 'French'
                    : 'English';

        const authorCredit = config.language === 'pt' ? 'Escrito por' : 'Written by';

        let taskDescription = "You are the Script Ingestion Engine. Rewrite the input into strict Master Scene (Fountain) format.";
        if (isContinuation) {
            taskDescription = "You are continuing the ingestion of a large script. Rewrite this segment into strict Fountain format. This chunk might start in the middle of a scene or dialogue. Maintain consistency.";
        }

        const prompt = `
            TASK: ${taskDescription}

            ${contextPrefix ? `
            [CONTINUITY CONTEXT - DO NOT REPEAT IN OUTPUT]
            The following text is the END of the previous chunk. Use it ONLY to maintain consistent character names and current scene context. DO NOT include this content in your output:
            "...${contextPrefix}"
            ` : ""}

            [FORMATTING STANDARDS - MASTER SCENE]
            ${getFormattingRules(config.language || 'en')}

            [REPAIR PROTOCOL]
            ${isFirstChunk ? `
            0. TITLE PAGE (Standardization):
               - Identify Title, Author, "Based on" (if any), and "Contact" (if any) information from the first page or header.
               - Format them at the very beginning using this EXACT structure:
                 [TITLE IN ALL CAPS]
                 ${authorCredit} [AUTHOR NAME]
                 Based on, [SOURCE]
                 Contact: [EMAIL] / [PHONE]
               - IMPORTANT: Put "${authorCredit}" and the Author's name on the SAME LINE.
               - Use a COMMA after "Based on," and "Baseado em,".
            ` : "0. NO TITLE PAGE: Do not repeat title or author. Start directly with script content."}
            1. GLUE RULE: Merge accidental line breaks inside dialogue.
            2. NARRATIVE LINK: Character+Extension (V.O./O.S.) must be followed by Dialogue.
            3. SYNTAX: Scene Headers (INT./EXT.), Action (Present tense), Character Names (UPPERCASE).
            4. NO NOISE: Strip page numbers and headers. 
            5. FIDELITY: Maintain 1:1 narrative parity. NO revisions.
            6. DESCRIPTIVE SUB-HEADINGS: Some screenplays use ALL CAPS titles as descriptive sub-headings within scenes (e.g. "THE EYE", "THE ROOM", "A HIGHWAY", "THE VOIGHT-KAMPFF MACHINE"). These are NOT character names — they introduce a new visual element or describe what we see. They must remain as Action lines in ALL CAPS. RULE: If an ALL CAPS line starts with an article (THE, A, AN, O, AS, OS, UM, UMA) and is followed by descriptive/narrative prose (not indented speech), treat it as an ACTION sub-heading, NOT a character cue.

            [CREATIVITY SETTING]
            Temperature: ${creativity.toFixed(1)}
            GUIDELINE: ${creativityGuideline}

            [LANGUAGE]
            Output in ${langLabel}.

            RAW TEXT:
            ${rawText}

            OUTPUT:
            Return ONLY raw Fountain text. No conversational filler.
        `;

        try {
            // Correct SDK Call Style for @google/genai
            const response = await ai.models.generateContent({
                model: modelName,
                contents: [{ role: 'user', parts: [{ text: prompt }] }],
                config: {
                    temperature: creativity,
                    topP: creativity > 0.1 ? 0.95 : 0.1,
                    topK: creativity > 0.1 ? 40 : 1
                }
            });

            if (!response.text) {
                console.error("[formatScriptSingle] Empty response from AI");
                return "";
            }

            return cleanScriptWhitespace(response.text);
        } catch (error) {
            console.error("[formatScriptSingle] Critical SDK Error:", error);
            throw error; // Let retryWithBackoff handle it or fail fast
        }
    });
}



/**
 * EXTRACTION 3.0: Smart Pre-flight Metadata
 * Extract Title, Author, Genre, Logline and Casting from the first few pages.
 */
export const extractScriptMetadata = async (
    rawText: string,
    config: AppState['config']
): Promise<{
    title: string;
    author: string;
    genre: string;
    logline: string;
    casting: string[];
}> => {
    return retryWithBackoff(async () => {
        const ai = await getAIInstance();
        const modelName = await getBestModel(ai);

        const langLabel = config.language === 'pt' ? 'Portuguese (BR)'
            : config.language === 'es' ? 'Spanish'
                : config.language === 'fr' ? 'French'
                    : 'English';

        const prompt = `
            TASK: Act as a Script Analyst. Extract metadata from the following script fragment.
            
            EXTRACT THE FOLLOWING:
            1. Title (The official name of the script)
            2. Author (The person who wrote it)
            3. Genre (Main genre: Drama, Thriller, Comedy, Sci-Fi, etc. Use 1-2 words max)
            4. Logline (A single high-impact sentence summarizing the core conflict/goal)
            5. Casting (Names of 3-5 main characters identified in this fragment)

            RULES:
            - If a field is not found, return "Unknown".
            - Return ONLY a VALID JSON object. No conversational filler.
            - Output content in ${langLabel}.

            SCRIPT FRAGMENT:
            ${rawText.substring(0, 5000)}

            OUTPUT FORMAT (JSON):
            {
                "title": "string",
                "author": "string",
                "genre": "string",
                "logline": "string",
                "casting": ["string"]
            }
        `;

        const response = await ai.models.generateContent({
            model: modelName,
            contents: [{ role: 'user', parts: [{ text: prompt }] }],
            config: {
                temperature: 0.0,
                responseMimeType: "application/json"
            }
        });

        if (response.text) {
            try {
                return JSON.parse(response.text);
            } catch (e) {
                console.error("[extractScriptMetadata] JSON Parse Error:", e, response.text);
                throw e;
            }
        }
        throw new Error("Empty response from AI during metadata extraction");
    });
};


export const runScriptAnalysis = async (
    scriptText: string,
    taskType: string,
    config: AppState['config']
): Promise<string> => {
    return retryWithBackoff(async () => {
        const ai = await getAIInstance();
        const lang = config.language;

        // 1. Get Localized Technical Instruction (e.g. "Analise os Arcos...")
        const instruction = getLocalizedTaskInstruction(taskType, lang);

        // 2. Define Language Label
        const langLabel = lang === 'pt' ? 'PORTUGUESE'
            : lang === 'es' ? 'SPANISH'
                : lang === 'fr' ? 'FRENCH'
                    : 'ENGLISH';

        // 3. Construct Robust Prompt with Output Rules
        let prompt = `
            *** CRITICAL OUTPUT RULES ***
            1. OUTPUT LANGUAGE: You MUST answer in ${langLabel} (${lang.toUpperCase()}).
            2. DO NOT TRANSLATE PROPER NAMES: Character names (e.g. "Willow", "Summer", "John") must remain exactly as they are in the script. Do not translate locations unless they are generic terms.
            3. FORMATTING: Use Markdown for readability (Bold for headers, bullet points for lists).
        `;

        if (taskType === 'plottwist') {
            prompt += `
            \n[PLOT TWIST THEORY]
            ${PLOT_TWIST_KNOWLEDGE}
            `;
        }

        if (taskType === 'scriptaudit') {
            prompt += `
            \n[SCRIPT AUDITING PROTOCOL]
            ${SCRIPT_AUDITING_KNOWLEDGE}
            `;
        }

        if (taskType === 'plant_and_payoff') {
            prompt += `
            \n[PLANT AND PAYOFF THEORY]
            ${PLANT_AND_PAYOFF_KNOWLEDGE}
            `;
        }

        prompt += `
            \n*** TASK ***
            ${instruction}

            *** SCRIPT CONTENT ***
            ${scriptText.substring(0, 30000)}
        `;

        const modelName = await getBestModel(ai);
        // Forced deterministic mode for Auditing and Tone Scanning, Creative for others
        const finalTemperature = (taskType === 'scriptaudit' || taskType === 'tone_analyzer') ? 0.0 : 1.0;

        const response = await ai.models.generateContent({
            model: modelName,
            contents: [{ role: 'user', parts: [{ text: prompt }] }],
            config: {
                temperature: finalTemperature,
                safetySettings: getSafetySettings(config)
            }
        });
        return response.text || "";
    });
};

export function calculateAuditScore(auditData: any) {
    let score = 100;
    let penaltyCount = 0;

    // Itera sobre a cadeia de eventos para deduzir pontos
    if (auditData.causality_chain && Array.isArray(auditData.causality_chain)) {
        auditData.causality_chain.forEach((node: any) => {
            // Furo grave: Evento sem gatilho (Deus Ex Machina ou conveniência)
            if (node.trigger_found === null) {
                score -= 15;
                penaltyCount++;
            }
            // Furo menor: Evento sem consequência (Beco sem saída narrativo)
            if (node.consequence === null) {
                score -= 5;
            }
        });
    }

    // Trava a nota no limite mínimo de 0
    score = Math.max(0, score);

    // Define o status visual para a UI
    let status = "Solid";
    if (score < 50) {
        status = "Critical";
    } else if (score < 80 || penaltyCount > 0) {
        status = "Attention";
    }

    return { score, status };
}

export const runPlotAudit = async (
    scriptText: string,
    userLanguageLabel: string
): Promise<PlotAuditResponse & { score: number, status: string }> => {
    const apiKey = "dummy-secure-proxy";

    const ai = await getAIInstance();
    const modelName = await getBestModel(ai);

    // Chamada API 1 (Extrator)
    const promptA = `Act as a forensic narrative extractor. Your ONLY job is to read the provided script text and extract the concrete sequence of events, actions, and character decisions.

STRICT GUARDRAILS:
1. DO NOT analyze, judge, or evaluate the quality of the text.
2. DO NOT include subtext, emotional states, or thematic elements unless they manifest as a direct physical action.
3. DO NOT summarize the plot. List the events chronologically.
4. Ignore formatting errors, typos, and camera directions.
5. The extracted list MUST be written strictly in ${userLanguageLabel}.

OUTPUT FORMAT:
Create a numbered list of raw facts.

Script Text:
${scriptText}`;

    const factList = await executeWithResilience(
        async (signal) => {
            const res = await ai.models.generateContent({
                model: modelName,
                contents: [{ role: 'user', parts: [{ text: promptA }] }],
                config: { temperature: 0.1 }
            });
            return res.text || "";
        },
        { maxRetries: 3, timeoutMs: 25000 }
    );

    // Chamada API 2 (Auditor)
    const promptB = `Act as a Senior Script Analyst specializing in structural logic and causality. 
Analyze the provided chronological list of narrative events (The Fact List) to perform a strict Plot Audit.

CORE DIRECTIVE: "Nothing happens because the script wants it. Everything happens because the story demands it."

AUDIT CRITERIA:
1. Causality Chain: Every significant event must have a logical trigger (prior cause) and a consequence. If an event happens purely by coincidence, it is a plot hole.
2. Justifiable Motivation: Characters must act based on established facts.
3. Rule Consistency: Solutions to problems cannot come from nowhere (Deus Ex Machina). 

GUARDRAILS (NON-INTERFERENCE):
- Do NOT suggest deleting scenes just because they seem slow; only flag them if they break causality.
- Do NOT invent external information to justify a plot hole. If the trigger isn't in the Fact List, it doesn't exist.
- If an event is purposefully ambiguous (mystery setup), classify it as a valid setup.

INSTRUCTIONS FOR JSON OUTPUT:
You must output a valid JSON object matching the exact schema provided in the API configuration. 
- All string values inside the JSON MUST be written strictly in ${userLanguageLabel}.
- Evaluate each major event. If you cannot find a clear trigger in the preceding facts, set "trigger_found" to null. This automatically constitutes a warning point.
- If there are items where "trigger_found" or "consequence" are null, set "requires_rewrite" to true.

The Fact List:
${factList}`;

    const resultAudit = await executeWithResilience(
        async (signal) => {
            const res = await ai.models.generateContent({
                model: modelName,
                contents: [{ role: 'user', parts: [{ text: promptB }] }],
                config: {
                    temperature: 0.1,
                    responseMimeType: "application/json",
                    responseSchema: {
                        type: "OBJECT",
                        properties: {
                            causality_chain: {
                                type: "ARRAY",
                                items: {
                                    type: "OBJECT",
                                    properties: {
                                        event: { type: "STRING" },
                                        trigger_found: { type: "STRING", nullable: true },
                                        consequence: { type: "STRING", nullable: true }
                                    },
                                    required: ["event"]
                                }
                            },
                            warning_points: {
                                type: "ARRAY",
                                items: { type: "STRING" }
                            },
                            strategic_suggestions: {
                                type: "ARRAY",
                                items: { type: "STRING" }
                            },
                            requires_rewrite: { type: "BOOLEAN" }
                        },
                        required: ["causality_chain", "warning_points", "strategic_suggestions", "requires_rewrite"]
                    }
                }
            });

            const rawText = res.text || "{}";

            try {
                const aiResult = JSON.parse(rawText);
                const { score, status } = calculateAuditScore(aiResult);
                return {
                    ...aiResult,
                    score,
                    status
                };
            } catch (err) {
                console.error("PlotAudit parse error", err);
                throw new Error("Failed to parse Plot Audit output");
            }
        },
        { maxRetries: 3, timeoutMs: 25000 }
    );

    return resultAudit;
};

export const runCharacterArcAnalysis = async (
    scriptText: string,
    characters: string[],
    config: AppState['config']
): Promise<string> => {
    return retryWithBackoff(async () => {
        const ai = await getAIInstance();
        const lang = config.language;
        const langLabel = lang === 'pt' ? 'PORTUGUESE'
            : lang === 'es' ? 'SPANISH'
                : lang === 'fr' ? 'FRENCH'
                    : 'ENGLISH';

        if (!characters || characters.length === 0) {
            throw new Error("No characters provided for analysis.");
        }

        const characterList = characters.join(', ');

        const prompt = `
            *** CRITICAL INSTRUCTION ***
            You are analyzing CHARACTER ARCS for a specific list of characters found in the script.
            
            STRICT CONSTRAINTS:
            1. ONLY analyze the characters listed here: [${characterList}].
            2. DO NOT create, infer, or mention any character NOT in this list.
            3. DO NOT output any introductory or concluding text (e.g., "Here are the arcs...", "Analysis complete").
            4. DO NOT provide a general summary or overall story thoughts.
            5. If a listed character has no significant presence, state that briefly in their dedicated section.
            6. Output MUST be in ${langLabel}.

            *** OUTPUT FORMAT ***
            Return a MARKDOWN list where each character is a primary header (### NAME). 
            ONLY output these blocks. No extra text allowed outside these blocks.
            
            For each character, provide:
            - **Arco Inicial**: (State at the beginning)
            - **Arco Atual**: (Development throughout the script so far)
            - **Conflito Interno**: (Psychological or emotional struggle)
            - **Direção**: (Where they seem to be heading)

            *** SCRIPT CONTENT ***
            ${scriptText.substring(0, 40000)}
        `;

        const modelName = await getBestModel(ai);
        const response = await ai.models.generateContent({
            model: modelName,
            contents: prompt,
        });

        return response.text || "";
    });
};

export const runCameraDirectionTask = async (
    scriptText: string,
    level: 'essential' | 'pro' | 'auteur',
    filterNoCamera: boolean,
    config: AppState['config']
): Promise<SceneCameraDirection[]> => {
    return retryWithBackoff(async () => {
        const ai = await getAIInstance();
        const lang = config.language;
        const langLabel = lang === 'pt' ? 'PORTUGUESE' : lang === 'es' ? 'SPANISH' : lang === 'fr' ? 'FRENCH' : 'ENGLISH';

        const prompt = `
            TASK: Suggest camera directions for each scene in the script.
            
            INPUT SCRIPT:
            ${scriptText.substring(0, 40000)}

            RULES:
            1. Segment the script into scenes based on headers.
            2. For each scene, analyze the dramatic objective, emotion, and action.
            3. LEVEL: ${level.toUpperCase()}. 
               - Essential: Basic shots (CU, MS, LS).
               - Pro: More dynamic movement and angles.
               - Auteur: Highly cinematic, subjective focus, and artistic movement.
            4. Suggest maximum 3 specific shots per scene.
            5. Use standardized cues based on the language ${langLabel}.
               Scale: ELS, LS, FS, MLS, MS, MCU, CU, ECU, INSERT, MACRO.
               Angle: EYE, HIGH, LOW, TOP, DUTCH.
               Move: STATIC, PAN, TILT, DOLLY IN, DOLLY OUT, TRUCK, CRANE, ZOOM IN, ZOOM OUT, ARC, ORBIT, HANDHELD, STEADICAM, GIMBAL, DRONE.
               Focus: SHALLOW, DEEP, RACK FOCUS.
            6. DO NOT invent new narrative elements.
            7. If filterNoCamera is true (${filterNoCamera}), prioritize scenes that currently lack camera directions.
            
            OUTPUT FORMAT (JSON ONLY, NO MARKDOWN):
            [
              {
                "sceneNumber": number,
                "slug": "INT. SCENE - DAY",
                "intention": "Short description of the scene's emotional goal (1 line)",
                "suggestions": [
                  {
                    "scale": "Standardized Scale",
                    "angle": "Standardized Angle",
                    "move": "Standardized Movement",
                    "focus": "Standardized Focus (optional)",
                    "justification": "Short justification (1 line)",
                    "cueText": "READY TO INSERT TEXT (e.g. 'CLOSE UP. HIGH ANGLE.')"
                  }
                ]
              }
            ]
            
            IMPORTANT: Use ${langLabel} for 'intention', 'justification', and 'cueText'.
        `;

        const modelName = await getBestModel(ai);
        const response = await ai.models.generateContent({
            model: modelName,
            contents: [{ role: 'user', parts: [{ text: prompt }] }],
            config: {
                responseMimeType: "application/json"
            }
        });

        if (response.text) {
            return JSON.parse(response.text);
        }
        throw new Error("No response.");
    });
};

export const generateElementDescription = async (
    scriptText: string,
    name: string,
    type: 'CHARACTER' | 'LOCATION' | 'PROP' | 'SFX' | 'CAMERA' | 'TRANSITION',
    config: AppState['config']
): Promise<string> => {
    return retryWithBackoff(async () => {
        const ai = await getAIInstance();
        const lang = config.language;
        const instruction = lang === 'pt' ? `Gere um perfil/descrição detalhada para: "${name}". Se faltarem detalhes no roteiro, infira criativamente mantendo o tom.`
            : lang === 'es' ? `Genera un perfil/descripción detallada para: "${name}".`
                : lang === 'fr' ? `Générez un profil/description détaillée pour : "${name}".`
                    : `Generate a detailed profile/description for: "${name}". If details are missing, infer them creatively.`;

        const prompt = `
            ${instruction}
            TYPE: ${type}
            SCRIPT CONTEXT:
            ${scriptText.substring(0, 20000)}
            
            OUTPUT LANGUAGE: ${lang.toUpperCase()}
        `;
        const modelName = await getBestModel(ai);
        const response = await ai.models.generateContent({
            model: modelName,
            contents: prompt,
            config: {
                safetySettings: getSafetySettings(config)
            }
        });
        return response.text?.trim() || "";
    });
};

export const improveElementDescription = async (
    currentDescription: string,
    config: AppState['config']
): Promise<string> => {
    return retryWithBackoff(async () => {
        const ai = await getAIInstance();
        const lang = config.language;

        const instruction = `Refine and improve this screenplay element description. 
        RULES:
        1. Improve clarity and writing style.
        2. Summarize (30-50% reduction in length).
        3. Maintain essential meaning and neutral/professional tone.
        4. DO NOT invent new facts.
        5. Maintain the ORIGINAL language of the provided text.
        6. Return ONLY the improved text.`;

        const prompt = `
            ${instruction}
            
            DESCRIPTION TO IMPROVE:
            "${currentDescription}"
        `;
        const modelName = await getBestModel(ai);
        const response = await ai.models.generateContent({
            model: modelName,
            contents: prompt,
            config: {
                safetySettings: getSafetySettings(config)
            }
        });
        return response.text?.trim() || currentDescription;
    });
};


export const estimateScriptDuration = async (
    scriptText: string,
    config: AppState['config']
): Promise<DurationEstimate> => {
    return retryWithBackoff(async () => {
        const ai = await getAIInstance();
        const langCode = config.language;
        const langInstruction = {
            'en': "Output text in English.",
            'pt': "Output text in Português do Brasil.",
            'es': "Output text in Español.",
            'fr': "Output text in Français."
        }[langCode] || "Output text in English.";

        // IMPORTANT: Sending full script text. Do not truncate.
        // Gemini 3 Flash allows ~1M tokens, sufficient for 200+ pages.
        const prompt = `
      TASK: Analyze the provided screenplay text and perform a comprehensive DURATION ESTIMATION based on SHOT DENSITY (PLANOS).
      
      INPUT SCRIPT:
      ${scriptText}

      ANALYSIS RULES:
      1. FULL SCAN: You MUST scan the ENTIRE text to count ALL scenes.
      2. SHOT ESTIMATION: For EACH SCENE, estimate the number of shots (planos) required to film it properly. 
         - A simple dialogue scene might have fewer shots.
         - An action scene might have many quick cuts (more shots).
         - Consider the "coverage" needed.
      3. PACING CLASSIFICATION: Classify the pacing of each scene:
         - "fast" (Action, intense dialogue, montage) -> shorter shots (~2.5 seconds avg).
         - "normal" (Standard dialogue, movement) -> standard shots (~4 seconds avg).
         - "slow" (Emotional moments, landscapes, establishing shots) -> longer shots (~6 seconds avg).
      4. CLASSIFICATION: Detect Format (Feature, Short, TV, Ad).
      
      CRITICAL REQUIREMENT - SCENE BREAKDOWN:
      - You MUST list EVERY single scene found in the script. 
      - Return 'estimatedShots' (integer) and 'pacing' (string) for each scene.

      OUTPUT FORMAT (JSON ONLY):
      Return a JSON object. Do NOT wrap in markdown code blocks.
      
      {
        "estimatedMinutes": number (float), // Approximate total
        "rangeLow": number (float),
        "rangeHigh": number (float),
        "classification": "string",
        "reasoning": "string",
        "sceneBreakdown": [
           {
             "slug": "string",
             "estimatedShots": number, // Integer count of estimated shots
             "pacing": "fast" | "normal" | "slow",
             "type": "string"
           }
        ]
      }

      LANGUAGE: ${langInstruction} for the 'classification' and 'reasoning' fields.
    `;

        const modelName = await getBestModel(ai);
        const response = await ai.models.generateContent({
            model: modelName,
            contents: prompt,
            config: {
                responseMimeType: "application/json"
            }
        });

        if (response.text) {
            try {
                const rawData = JSON.parse(response.text);

                // POST-PROCESSING: Calculate exact duration based on shots
                const processedSceneBreakdown = [];
                let calculatedTotalMinutes = 0;

                const SHOT_DURATION_SEC = {
                    fast: 2.5,
                    normal: 4.0,
                    slow: 6.0
                };

                for (const scene of (rawData.sceneBreakdown || [])) {
                    const pacingKey = (scene.pacing || 'normal').toLowerCase() as keyof typeof SHOT_DURATION_SEC;
                    const avgDuration = SHOT_DURATION_SEC[pacingKey] || 4.0;

                    // Fallback: If 0 estimated shots, assume at least 1-2 shots for a header + action
                    const shotCount = (scene.estimatedShots && scene.estimatedShots > 0) ? scene.estimatedShots : Math.max(2, Math.ceil((scene.slug.length + 50) / 100));

                    const durationSeconds = shotCount * avgDuration;
                    const durationMinutes = durationSeconds / 60;

                    // Enforce minimum 0.1 min (6 sec)
                    const finalMinutes = Math.max(0.1, durationMinutes);

                    calculatedTotalMinutes += finalMinutes;

                    processedSceneBreakdown.push({
                        sceneSlug: scene.slug || "UNKNOWN SCENE", // Map slug to expected 'sceneSlug' if needed, or keeping structure flexible
                        slug: scene.slug, // Keep original
                        estimatedMinutes: finalMinutes,
                        estimatedTime: `${shotCount} shots`, // Legacy field support if UI needs text
                        reasoning: `${shotCount} shots (${scene.pacing || 'normal'})`, // Context for user
                        pacing: scene.pacing,
                        estimatedShots: shotCount
                    });
                }

                return {
                    estimatedMinutes: calculatedTotalMinutes, // Strict sum
                    sceneBreakdown: processedSceneBreakdown,
                    reasoning: rawData.reasoning || `Total calculated from ${processedSceneBreakdown.length} scenes based on shot density.`
                } as any; // Casting to fit DurationEstimate loosely or strict if updated types

            } catch (e) {
                console.error("JSON Parse Error on Duration Estimate", e);
                throw new Error("Failed to parse duration estimate.");
            }
        }
        throw new Error("No response generated.");
    });
};

export const generateProductionPrompts = async (
    scriptText: string,
    config: AppState['config']
): Promise<SceneShotList[]> => {
    return retryWithBackoff(async () => {
        const ai = await getAIInstance();
        const lang = config.language;
        const langLabel = lang === 'pt' ? 'PORTUGUESE' : lang === 'es' ? 'SPANISH' : lang === 'fr' ? 'FRENCH' : 'ENGLISH';

        const prompt = `
            TASK: Analyze the provided script and generate a professional SHOT LIST with prompts for 4 specific AI models (Midjourney, Nano Banana Pro, Kling, Veo 3).

            INPUT SCRIPT:
            ${scriptText}

            INSTRUCTIONS:
            1.  **DECOUPAGE:** Break the script into SCENES. For each scene, break it down into SHOTS (plans/takes).
            2.  **STRICT ADHERENCE:** Do NOT invent elements not present in the script. You may infer cinematic language (camera, lens, lighting) but NOT new narrative objects or events.
            3.  **LOCKS:** Maintain visual consistency (character features, world look, palette) across all shots in a scene.
            4.  **LANGUAGE CONSISTENCY (MANDATORY):**
                - **"visualAction" field:** MUST be in ${langLabel}. This is for the UI description in the director's interface.
                - **"prompts" fields (midjourney, nano, kling, veo):** MUST be 100% in ENGLISH (US).
                - **TRANSLATE:** If the source script is in ${langLabel}, you MUST translate all content, effects, and technical terms to English for the prompts.
            5.  **LANGUAGE AUDIT:** Scan the generated prompts. If words like "DE", "COM", "AÇÃO", "CHUVA", "LUZ" appear in the prompts, translate them to English.
            6.  **PROMPT FORMATTING RULES:**
                *   **Midjourney:** [SUBJECT], [ATTRIBUTES], [ACTION/POSE], [ENVIRONMENT], [LIGHTING], [CAMERA/OPTICS], [COMPOSITION], [STYLE], [MOOD] --ar 16:9 --v 7 --s 100 --q 1 --no text, watermark
                *   **Nano Banana Pro:** Create an image of [SUBJECT + DETAILS]. [ACTION]. Environment: [ENVIRONMENT]. Composition: [SHOT TYPE + ANGLE + FOCUS/DOF]. Light: [LIGHTING]. Style: [STYLE]. Quality: 4K, high sharpness. Aspect Ratio: 16:9.
                *   **Kling:** [SUBJECT + details], [MOVEMENT], in/at [SCENE + details]. [SHOT TYPE + lens + DOF + composition]. Camera: [movement]. Lighting: [lighting]. Atmosphere: [atmosphere]. Style: [style]. EFFECTS: [grouped effects].
                *   **Veo 3:** [SUBJECT + details]. Action: [ACTION]. Environment: [ENVIRONMENT]. Camera: [SHOT TYPE + LENS + DOF] + [MOVEMENT]. Light: [LIGHTING]. Atmosphere: [ATMOSPHERE]. Style: [STYLE]. EFFECTS: [grouped effects]. (Full English, 24fps).

            7. **INSTRUCTIONS FOR "EFFECTS:" BLOCK (Kling/Veo 3 ONLY):**
                - If the scene/shot contains clear SFX, VFX or practical effects, include an "EFFECTS:" block.
                - Use English headers: "SFX:", "VFX:", "PRACTICAL/IN-CAMERA:".
                - Translate all effect names to English.

            OUTPUT FORMAT (JSON ONLY):
            Return an ARRAY of objects. Do NOT wrap in markdown.
            [
              {
                "sceneNumber": 1,
                "slug": "INT. OFFICE - DAY",
                "shots": [
                  {
                    "id": 1,
                    "shotType": "Medium Shot", 
                    "visualAction": "...",
                    "prompts": {
                      "midjourney": "...",
                      "nano": "...",
                      "kling": "...",
                      "veo": "..."
                    }
                  }
                ]
              }
            ]
        `;

        const modelName = await getBestModel(ai);
        const response = await ai.models.generateContent({
            model: modelName, // Using Pro for complex reasoning/cinematography
            contents: prompt,
            config: {
                responseMimeType: "application/json",
            }
        });

        if (response.text) {
            try {
                return JSON.parse(response.text);
            } catch (e) {
                console.error("JSON Parse Error on Production Prompts", e);
                throw new Error("Failed to parse prompt generation.");
            }
        }
        throw new Error("No response generated.");
    });
};

export const generateShotList = async (
    sceneSlug: string,
    content: string, // This is either the full scene text or a single shot text
    config: AppState['config'],
    sceneContext?: string, // Optional full scene context for shot mode
    effects?: EffectSuggestion[] // New: Structured effects from analysis
): Promise<SceneShotList> => {
    return retryWithBackoff(async () => {
        const ai = await getAIInstance();
        const sceneNumMatch = sceneSlug.match(/^(\d+)/);
        const sceneNum = sceneNumMatch ? parseInt(sceneNumMatch[1]) : 0;

        const isShotMode = !!sceneContext;
        const lang = config.language;
        const langLabel = lang === 'pt' ? 'PORTUGUESE' : lang === 'es' ? 'SPANISH' : lang === 'fr' ? 'FRENCH' : 'ENGLISH';

        const prompt = `
            You are an expert AI Cinematographer and Director (Auteur.OS).
            
            TASK: Generate a professional SHOT LIST with prompts for 4 specific AI models (Midjourney, Nano Banana Pro, Kling, Veo 3).
            
            ${isShotMode ? `
            MODE: SHOT/PLANO
            You are generating prompts for a SPECIFIC SELECTED PARAGRAPH.
            
            FULL SCENE CONTEXT (for continuity/coherence):
            ${sceneContext}
            
            SPECIFIC CONTENT TO VISUALIZE:
            "${content}"
            
            CRITICAL: Even if the content is short, you MUST generate exactly ONE high-quality cinematic shot in the "shots" array that best represents this specific text. DO NOT return an empty list.
            ` : `
            MODE: SCENE
            You are generating a list of 3 to 6 key shots for this entire scene.
            SCENE CONTENT:
            ${content}
            `}

            ${effects && effects.length > 0 ? `
            SUGGESTED EFFECTS (Detected from Script):
            ${effects
                    .filter(e => e.evidence && e.confidence >= 60)
                    .slice(0, 6)
                    .map(e => `- [${e.type}] ${e.name} (Evidence: "${e.evidence}", Confidence: ${e.confidence}%)`)
                    .join('\n')}
            ` : ''}

            CRITICAL RULES:
            1. **STRICT ADHERENCE:** Do NOT invent narrative elements or events not present in the ${isShotMode ? 'shot content' : 'scene content'}. 
            2. **INFERENCE:** You may infer cinematic language (lens, movement, rhythm, blocking, focus, lighting) to enhance the visual, but keep 100% of the original text as the factual base.
            3. **CONTINUITY:** Maintain visual consistency (characters, locations, palette) with the ${isShotMode ? 'scene context' : 'script'}.
            4. **MANDATORY OUTPUT:** You MUST return at least one shot. If you are in SHOT MODE, you MUST return exactly one shot.
            5. **LANGUAGE CONSISTENCY (MANDATORY):**
               - **"visualAction" field:** MUST be in ${langLabel}. This is the UI description for the director.
               - **"prompts" fields (midjourney, nano, kling, veo):** MUST be 100% in ENGLISH (US).
               - **TRANSLATE:** If the source script or SUGGESTED EFFECTS are in ${langLabel}, you MUST translate them to English for the prompts.
               - **Example:** If an effect is "TRAVAMENTO DE MANDÍBULAS", use "JAW LOCK SNAP" in the prompts.
            6. **LANGUAGE AUDIT:** Before finishing, scan the prompts. If you find words like "DE", "COM", "AÇÃO", "CHUVA", "MANDÍBULAS", "LUZ", you FAILED. Translate everything to English.

            PROMPT FORMATTING RULES:
            - **Midjourney (V7):** [SUBJECT], [ATTRIBUTES], [ACTION/POSE], [ENVIRONMENT], [LIGHTING], [CAMERA/OPTICS], [COMPOSITION], [STYLE], [MOOD] --ar 16:9 --v 7 --s 100 --q 1 --no text, watermark
            - **Nano Banana Pro (Flux):** Create an image of [SUBJECT + DETAILS]. [ACTION]. Environment: [ENVIRONMENT]. Composition: [SHOT TYPE + ANGLE + FOCUS/DOF]. Light: [LIGHTING]. Style: [STYLE]. Quality: 4K, high sharpness. Aspect Ratio: 16:9.
            - **Kling:** [SUBJECT + details], [MOVEMENT], in/at [SCENE + details]. [SHOT TYPE + lens + DOF + composition]. Camera: [movement]. Lighting: [lighting]. Atmosphere: [atmosphere]. Style: [style]. EFFECTS: [grouped effects].
            - **Veo 3:** [SUBJECT + details]. Action: [ACTION]. Environment: [ENVIRONMENT]. Camera: [SHOT TYPE + LENS + DOF] + [MOVEMENT]. Light: [LIGHTING]. Atmosphere: [ATMOSPHERE]. Style: [STYLE]. EFFECTS: [grouped effects]. (Full English, 24fps).

            INSTRUCTIONS FOR "EFFECTS:" BLOCK (Kling/Veo 3 PROMPTS ONLY):
            1. You MUST include a block starting with "EFFECTS:" at the end of the prompt if (and only if) there are relevant "SUGGESTED EFFECTS" matching this shot/scene.
            2. Group the relevant effects by type exactly using these headers (IN ENGLISH):
               - "SFX:" (for sound effects names)
               - "VFX:" (for visual/digital effects names)
               - "PRACTICAL/IN-CAMERA:" (for practical effects names)
            3. Use the English-translated names of the suggested effects.
            4. Only include items that have high confidence (>= 60) and clear textual evidence in the script.
            5. If no suggested effects are relevant or available for this scene, OMIT the "EFFECTS:" block entirely.
            
            EXAMPLE BLOCK:
            EFFECTS:
            SFX: Thunder, Heavy Rain, Footsteps in mud.
            VFX: Spark particles, Digital blur.
            PRACTICAL/IN-CAMERA: Real smoke, Lens flare.

            OUTPUT FORMAT (JSON ONLY):
            {
                "sceneNumber": ${sceneNum},
                "slug": "${sceneSlug.replace(/"/g, '\\"')}",
                "shots": [
                    {
                        "id": 1,
                        "shotType": "e.g., Medium Shot",
                        "visualAction": "Brief factual description of what happens in this shot",
                        "prompts": {
                            "midjourney": "...",
                            "nano": "...",
                            "kling": "...",
                            "veo": "..."
                        }
                    }
                ]
            }
        `;

        const modelName = await getBestModel(ai);
        console.log(`[AI] Generating shot list with model: ${modelName}`);
        const response = await ai.models.generateContent({
            model: modelName,
            contents: [{ role: 'user', parts: [{ text: prompt }] }],
            config: {
                responseMimeType: "application/json",
                safetySettings: getSafetySettings(config)
            }
        });

        if (response.text) {
            const result: SceneShotList = JSON.parse(response.text);
            if (!result.shots || result.shots.length === 0) {
                console.warn("[aiService] AI returned empty shots array for:", sceneSlug);
                throw new Error("AI failed to generate any shots for this scene.");
            }
            return result;
        }
        throw new Error("No response.");
    });
};

export const runEffectsTask = async (
    scriptText: string,
    config: AppState['config']
): Promise<SceneEffectsResult[]> => {
    return retryWithBackoff(async () => {
        const ai = await getAIInstance();
        const lang = config.language;
        const langLabel = lang === 'pt' ? 'PORTUGUESE' : lang === 'es' ? 'SPANISH' : lang === 'fr' ? 'FRENCH' : 'ENGLISH';

        const prompt = `
            TASK: Suggest SFX, VFX, and practical effects for each scene in the script.
            
            RULES:
            1. Segment the script into scenes based on headers.
            2. For each scene, analyze actions, descriptions, environment, and events.
            3. SUGGEST ONLY if there is textual evidence (e.g., "Thunder rolls" -> SFX: Thunder). 
            4. DO NOT INVENT anything not supported by the text.
            5. Max 10 total suggestions per scene.
            6. Output in ${langLabel}.

            OUTPUT FORMAT (JSON ONLY, NO MARKDOWN):
            [
              {
                "sceneId": "string (index or slug)",
                "sceneHeading": "string",
                "suggestions": [
                  {
                    "type": "SFX" | "VFX" | "EFFECTS",
                    "name": "Short name",
                    "why": "1 objective sentence why",
                    "evidence": "Short snippet from script",
                    "intensity": "low" | "medium" | "high",
                    "confidence": number (0-100)
                  }
                ]
              }
            ]
            
            SCRIPT CONTENT:
            ${scriptText.substring(0, 40000)}
        `;

        const modelName = await getBestModel(ai);
        console.log(`[AI] Running effects task with model: ${modelName}`);
        const response = await ai.models.generateContent({
            model: modelName,
            contents: [{ role: 'user', parts: [{ text: prompt }] }],
            config: {
                responseMimeType: "application/json",
                safetySettings: getSafetySettings(config)
            }
        });

        if (response.text) {
            return JSON.parse(response.text);
        }
        throw new Error("No response.");
    });
};

/**
 * Utility to translate content at runtime using Gemini.
 * Primarily used to ensure UI parity (matching uiLocale)
 * or prompt purity (forcing en-US).
 */
export const translate = async (text: string, targetLocale: string): Promise<string> => {
    return retryWithBackoff(async () => {
        const ai = await getAIInstance();
        const prompt = `Translate the following text to ${targetLocale}. 
        Return ONLY the translated text, no quotes, no explanations.
        TEXT: ${text}`;

        const modelName = await getBestModel(ai);
        const response = await ai.models.generateContent({
            model: modelName,
            contents: [{ role: 'user', parts: [{ text: prompt }] }]
        });

        return response.text?.trim() || text;
    });
};

export const improveTextSection = async (
    fullScript: string,
    sectionContent: string,
    taskType: string,
    config: AppState['config']
): Promise<string> => {
    return retryWithBackoff(async () => {
        const ai = await getAIInstance();
        const lang = config.language;
        const langLabel = lang === 'pt' ? 'PORTUGUESE' : lang === 'es' ? 'SPANISH' : lang === 'fr' ? 'FRENCH' : 'ENGLISH';

        const prompt = `
            CONTEXT (Full Script Summary):
            ${fullScript.substring(0, 5000)}...

            SECTION TO IMPROVE:
            "${sectionContent}"

            TASK TYPE: ${taskType}

            INSTRUCTION:
            Rewrite the section above to be more insightful, precise, and helpful for the writer. Isolate the specific advice and improve it.
            If it involves Character Arcs, deepen the psychological analysis and character growth.
            If it involves Plot, make it more logical and structural.
            If it involves Continuity, identify the discrepancies and propose specific textual corrections to fix them. Ensure each error has a bolded label (e.g., "**Suggestion**" in ${langLabel}).
            
            OUTPUT RULES:
            - Maintain the same format/style (Markdown).
            - Output ONLY the improved text for this section.
            - DO NOT repeat the character header (### NAME) if it was present.
            - DO NOT add conversational filler (no "Here is...", no "Improved:").
            - Write in ${langLabel}.
        `;

        const modelName = await getBestModel(ai);
        const response = await ai.models.generateContent({
            model: modelName,
            contents: [{ role: 'user', parts: [{ text: prompt }] }],
            config: {
                safetySettings: getSafetySettings(config)
            }
        });

        return response.text?.trim() || sectionContent;
    });
};

export const runContinuityAudit = async (
    scriptText: string,
    characterBible: CharacterProfile[],
    config: AppConfig
): Promise<string> => {
    return retryWithBackoff(async () => {
        const ai = await getAIInstance();
        const lang = config.language;
        const langLabel = lang === 'pt' ? 'Portuguese (Português do Brasil)'
            : lang === 'es' ? 'Spanish (Español)'
                : lang === 'fr' ? 'French (Français)'
                    : 'English';

        const taskDesc = "Audit the script for temporal, spatial, and object logic flaws.";

        const rulesDesc = `
VIOLATION TYPES TO DETECT:
1. TIMELINE ERRORS: Chronological errors (e.g., DAY scene follows NIGHT scene without transition; clock shows 10 AM and then 6 PM in the same scene).
2. OBJECT PERMANENCE: Objects that disappear or appear (e.g., "Draws the gun" but never holstered it; "Glass is full" immediately after drinking everything).
3. CAUSALITY/PHYSICS: Impossible actions in the environment (e.g., "Exits through the window" in an underground bunker; "Rains" inside the house).
`;

        const systemInstruction = `
Role: AUTEUR.OS WORLD LOGIC SUPERVISOR
Task: ${taskDesc}

STRICT LANGUAGE RULE:
- Your response (description, suggestion) MUST be 100% in ${langLabel}.
- Do NOT output English if ${langLabel} is not English.

IGNORE: Do not analyze psychology or physical traits of human characters.

${rulesDesc}

OUTPUT SCHEMA (JSON):
[
  {
    "type": "Timeline | Object | World Logic",
    "description": "Description of the logistical error (Target language: ${langLabel}).",
    "suggestion": "Suggestion for correction (Target language: ${langLabel})."
  }
]

STRICT RULES:
- Output ONLY the JSON array. No conversational text.
- If no violations are found, return an empty array [].
- Be precise and objective.
`;

        const userPrompt = `
[THE SCRIPT]
${scriptText}
`;

        const modelName = await getBestModel(ai);
        const response = await ai.models.generateContent({
            model: modelName,
            contents: [{ role: 'user', parts: [{ text: userPrompt }] }],
            config: {
                systemInstruction: systemInstruction,
                responseMimeType: "application/json",
                safetySettings: getSafetySettings(config)
            }
        });

        const rawJson = response.text || "[]";

        try {
            const violations = JSON.parse(rawJson);
            if (!Array.isArray(violations) || violations.length === 0) {
                return ""; // Return empty string to indicate no issues
            }

            // Convert JSON array to Markdown format for AiTaskModal
            return violations.map(v => {
                return `### ${v.type}\n${v.description}\n\n${v.suggestion}`;
            }).join('\n\n');
        } catch (e) {
            console.error("[Continuity Audit] JSON Parse Error:", e);
            return "";
        }
    });
};

export const runCharacterDiagnostic = async (
    scriptText: string,
    characterBible: CharacterProfile[],
    config: AppConfig
): Promise<string> => {
    return retryWithBackoff(async () => {
        const ai = await getAIInstance();
        const lang = config.language;
        const langLabel = lang === 'pt' ? 'Portuguese (Português do Brasil)'
            : lang === 'es' ? 'Spanish (Español)'
                : lang === 'fr' ? 'French (Français)'
                    : 'English';

        const t = (TRANSLATIONS[lang] || TRANSLATIONS.en) as typeof TRANSLATIONS.en;
        const discrepancyPrompt = t.char_diagnostic_discrepancy_prompt || "Script indicates [X], while Profile indicates [Y]. What do you want?";

        const taskDesc = 'Ensure characters act and speak according to their defined profiles in the user\'s primary language.';

        const jsonDescriptions = {
            issue: `Technical description of the conflict in ${langLabel}.`,
            option_a: "Just the corrected script snippet or exact instruction.",
            option_b: "Textual description of the profile change."
        };

        const systemInstruction = `
Role: AUTEUR.OS CHARACTER PSYCHOLOGY SUPERVISOR
Task: ${taskDesc}

STRICT LANGUAGE RULE:
- Your response (issue, discrepancy, option_a_script, option_b_profile) MUST be 100% in ${langLabel}.
- Do NOT output English if ${langLabel} is not English.

VIOLATION TYPES TO DETECT:
1. PHYSICAL MISMATCH (Identidade): Text contradicts character's body, age, physiology, or origin.
2. VOICE/DIALOGUE (Expressão): Speech style, topics, or sense of humor does not match profile (e.g. slang vs formal).
3. BEHAVIORAL BREAK (Psicologia): Actions contradict character archetype, traits, moral code, or goals.
4. DATA INTEGRITY: Wrong age, name, or profession in text.

CRITICAL INSTRUCTIONS:
- You MUST analyze EVERY character provided in the [CHARACTER BIBLE] against the provided [THE SCRIPT].
- **TOTAL DIAGNOSTIC:** For each character, you MUST check all three fields: **Identidade (Identity)**, **Psicologia (Psychology)**, and **Expressão (Expression)**.
- **CONSOLIDATION:** If a character has multiple inconsistencies (e.g., an Identity mismatch AND an Expression mismatch), you MUST consolidate them into ONE single entry for that character.
- The 'issue' field should list all found inconsistencies clearly.
- 'option_a_script' should provide a consolidated instruction to fix the script for ALL identified issues.
- 'option_b_profile' should provide a consolidated description of all changes needed in the profile.
- 'option_b_json' MUST include updates for ALL affected sections (identity, psychology, expression). If a section has no change, you can omit it or provide the current value, but the provided section must be complete for any field you change.

OUTPUT SCHEMA (JSON):
[
  {
    "character_name": "NAME",
    "issue": "${jsonDescriptions.issue}",
    "discrepancy": "${discrepancyPrompt}",
    "option_a_script": "${jsonDescriptions.option_a}",
    "option_b_profile": "${jsonDescriptions.option_b}",
    "option_b_json": {
      "data": {
        "identity": { 
            "full_name": "string",
            "age_range": "string", 
            "profession_or_role": "string",
            "social_class": "string",
            "era_setting": "string",
            "current_location": "string",
            "is_real_person": "boolean"
        },
        "psychology": { 
            "archetype": "string", 
            "personality_traits": ["string"],
            "moral_code_or_stance": "string", 
            "core_fears_or_pains": "string",
            "primary_goal": "string" 
        },
        "expression": { 
            "speech_style": "string", 
            "conversation_topics": ["string"],
            "sense_of_humor": "string",
            "visual_description": "string" 
        }
      }
    }
  }
]

STRICT RULES:
- Output ONLY the JSON array. No conversational text.
- Do NOT wrap the output in markdown code blocks.
- Escape double quotes within text fields.
- Option A (option_a_script) MUST focus ONLY on instructions to EDIT the SCRIPT to match the character profile. Explain what to change and why.
- Option B (option_b_profile) MUST focus ONLY on instructions to UPDATE the CHARACTER PROFILE to accept the new behavior/voice shown in the script.
- **CRITICAL:** DO NOT swap Option A and Option B. Option A is for SCRIPT, Option B is for PROFILE.
- **CRITICAL OPTION B JSON RULE:** When providing 'option_b_json', you MUST provide the FULL object for any section you wish to change ('identity', 'psychology', or 'expression').
- For 'personality_traits' and 'conversation_topics', you MUST provide the COMPLETE new array.
`;

        const userPrompt = `
        [CHARACTER BIBLE]
            ${JSON.stringify(characterBible, null, 2)
            }

[THE SCRIPT]
${scriptText}
        `;

        const modelName = await getBestModel(ai);
        const response = await ai.models.generateContent({
            model: modelName,
            contents: [{ role: 'user', parts: [{ text: userPrompt }] }],
            config: {
                systemInstruction: systemInstruction,
                responseMimeType: "application/json",
                safetySettings: getSafetySettings(config)
            }
        });

        let rawJson = response.text || "[]";

        // Robust JSON Extraction: Try to find legal JSON array pattern
        const jsonMatch = rawJson.match(/\[[\s\S]*\]/);
        if (jsonMatch) {
            rawJson = jsonMatch[0];
        } else {
            // Fallback: simple markdown cleanup
            rawJson = rawJson.replace(/```json\s * /ig, '').replace(/```\s*/g, '').trim();
        }

        try {
            const violations = JSON.parse(rawJson);
            if (!Array.isArray(violations) || violations.length === 0) {
                return "";
            }

            // Backend Deduplication: Keep only the first violation per character name
            const uniqueViolations: any[] = [];
            const seenCharacters = new Set<string>();

            for (const v of violations) {
                const charKey = v.character_name?.toUpperCase().trim();
                if (charKey && !seenCharacters.has(charKey)) {
                    seenCharacters.add(charKey);
                    uniqueViolations.push(v);
                }
            }

            return uniqueViolations.map(v => {
                const bJson = JSON.stringify(v.option_b_json);
                return `### ${v.character_name}\n${v.issue}\n\n[[OPTION_A]]\n${v.option_a_script}\n\n[[OPTION_B]]\n${v.option_b_profile}\n\n[[OPTION_B_JSON]]\n${bJson}`;
            }).join('\n\n');
        } catch (e) {
            console.error("[Character Diagnostic] JSON Parse Error:", e);
            return "";
        }
    });
};

export const checkCharacterConsistency = async (
    paragraph: string,
    characterBible: CharacterProfile[],
    config: AppConfig
): Promise<string[]> => {
    return retryWithBackoff(async () => {
        const ai = await getAIInstance();
        const lang = config.language;
        const langLabel = lang === 'pt' ? 'Portuguese' : 'English';

        // Filter character bible to only include relevant characters (those whose names appear in the paragraph)
        const upperParagraph = paragraph.toUpperCase();
        const relevantCharacters = characterBible.filter(char =>
            upperParagraph.includes(char.name.toUpperCase())
        );

        if (relevantCharacters.length === 0) return [];

        const systemInstruction = `
Role: CHARACTER CONSISTENCY ANALYST
Task: Check if the provided paragraph contradicts the character's profile (Identity, Psychology, Expression).

STRICT OUTPUT RULE:
- Return ONLY a JSON array of character names that have inconsistencies.
- If no inconsistencies are found, return [].
- DO NOT provide explanations or conversational text.

VIOLATION TYPES:
1. IDENTITY: Contradicting age, profession, or physical traits.
2. PSYCHOLOGY: Contradicting archetype, traits, or moral code.
3. EXPRESSION: Contradicting speech style or vocabulary.

EXAMPLE: ["Character Name"]
`;

        const userPrompt = `
[CHARACTER BIBLE]
${JSON.stringify(relevantCharacters, null, 2)}

[PARAGRAPH TO CHECK]
"${paragraph}"

Consistent with ${langLabel}.
`;

        const modelName = await getBestModel(ai);
        const response = await ai.models.generateContent({
            model: modelName,
            contents: [{ role: 'user', parts: [{ text: userPrompt }] }],
            config: {
                systemInstruction: systemInstruction,
                responseMimeType: "application/json",
                safetySettings: getSafetySettings(config)
            }
        });

        const rawJson = response.text || "[]";
        try {
            const results = JSON.parse(rawJson);
            return Array.isArray(results) ? results : [];
        } catch (e) {
            console.error("[Check Consistency] JSON Parse Error:", e);
            return [];
        }
    });
};

export const auditScript = async (
    scriptContent: string,
    appLanguage: string,
    previousAudit?: string,
    round: number = 1,
    startLine: number = 1,
    verifiedSegments: string[] = [],
    temperature: number = 0.0, // Always literal for technical auditing
    config?: AppConfig,
    fixedIssues?: { original: string; suggestion: string; rule: string }[],
    format: string = 'Film'
): Promise<{ content: string; debugLog?: string }> => {
    return retryWithBackoff(async () => {
        const ai = await getAIInstance();
        const targetLangDescription = LANGUAGE_MAP[appLanguage] || "English";

        // CODES TO SILENCE: Purely aesthetic formatting handled by the system.
        const silentCodes = [...MECHANICAL_RULES_EXCLUSION];

        // Methodology Logic
        let methodologySection = "";
        if (config) {
            if (config.methodMcKee) {
                methodologySection += `\n### ROBERT MCKEE ANALYSIS RULES (STR-01):\n${METHOD_MCKEE_RULES}\nInstruction: If a scene has no Value Shift, flag as STR-01.`;
            } else {
                silentCodes.push('STR-01');
            }
            if (config.methodField) {
                methodologySection += `\n### SYD FIELD ANALYSIS RULES (STR-02):\n${METHOD_FIELD_RULES}\nInstruction: If characters lack Dramatic Need/Goal, flag as STR-02.`;
            } else {
                silentCodes.push('STR-02');
            }
            if (config.structuralAnalysis) {
                methodologySection += `\n### STRUCTURAL CONFLICT ANALYSIS (STR-03 / STR-04):\n- Every scene MUST have conflict/opposition. If absent, flag as STR-03.\n- Check for Static Pacing (STR-04).`;
            } else {
                silentCodes.push('STR-03', 'STR-04');
            }

            if (config.customMethods && config.customMethods.length > 0) {
                config.customMethods.forEach(method => {
                    if (method.isActive) {
                        methodologySection += `\n### CUSTOM METHODOLOGY: ${method.name}\n${method.content}`;
                    }
                });
            }
        } else {
            silentCodes.push('STR-01', 'STR-02', 'STR-03', 'STR-04');
        }

        const localizedMap = LOCALIZED_ERRORS[appLanguage] || AUDIT_ERRORS;
        const englishMap = AUDIT_ERRORS_EN;
        const activeErrors = Object.values(englishMap).filter(err => {
            if (round === 1 && ['DLG-03', 'ACT-13'].includes(err.code)) return true;
            return !silentCodes.includes(err.code);
        });
        const rulesSummary = `[SYSTEM] ACTIVE AUDIT RULES:\n` + activeErrors.map(err => `- [${err.code}] ${err.title}: ${err.description}`).join('\n') + `\n\n---`;

        if (scriptContent.trim().length < 50) {
            return {
                content: JSON.stringify({
                    score: 100,
                    issues: [],
                    status: "Script too short for analysis"
                }),
                debugLog: rulesSummary + "\nScript too short for analysis"
            };
        }

        // 🛡️ ITERATIVE WORKFLOW LOGIC
        let auditContext = "";
        let modeInstruction = "";

        // Build corrections memory from fixedIssues (accumulated across all rounds)
        const hasFixedIssues = fixedIssues && fixedIssues.length > 0;
        let correctionsMemory = "";
        if (hasFixedIssues) {
            correctionsMemory = `
### MEMORY OF APPLIED CORRECTIONS (${fixedIssues!.length} corrections)  
**ABSOLUTE RULE**: The blocks listed below HAVE ALREADY BEEN CORRECTED in previous rounds.  
The CURRENT script text already contains these corrections. You **MUST NOT** re-audit or report errors in these corrected snippets.  
If the corrected snippet is now correct, **ACCEPT IT and move on**.  
Only report an error in a previously corrected snippet if the correction INTRODUCED a NEW and DIFFERENT error.

${fixedIssues!.map((fix, i) => `[Correction ${i + 1}] Code: ${fix.rule}
  - Original Text (with error): "${fix.original.substring(0, 200)}"
  - Applied Correction: "${fix.suggestion.substring(0, 200)}"`).join('\n')}
`;
        }

        if (round > 1) {
            // Parse previous audit for context if available
            if (previousAudit) {
                try {
                    const prev = JSON.parse(previousAudit);
                    const prevIssues = prev.issues || [];
                    const prevScore = prev.score || 0;
                    auditContext = `
### CONTEXT FROM PREVIOUS AUDIT (ROUND ${round - 1})
- Previous Score: ${prevScore}/100
- Total Flaws Identified: ${prevIssues.length}
- Flaws SELECTED and CORRECTED: ${hasFixedIssues ? fixedIssues!.length : 0}
`;
                } catch (e) {
                    console.error("[auditScript] Failed to parse previous audit.", e);
                }
            }

            const isFinalRound = round >= 3;
            modeInstruction = `
### MODE: ITERATIVE AUDIT — ${isFinalRound ? 'FINAL ROUND (FORCED CONVERGENCE)' : `ROUND ${round} of 3`}
[LOGIC KERNEL: VERIFICATION & RESIDUAL SCAN]

1. This is Round ${round}. A CORRECTED version of the script was generated by AI based on previous flaws.
2. **CORRECTED BLOCKS**: The blocks listed in the CORRECTIONS MEMORY have already been handled. The current text ALREADY reflects the corrections. DO NOT report these blocks again UNLESS the correction introduced a NEW and GRAVE error.
3. **FOCUS**: Audit ONLY the blocks that WERE NOT touched by previous corrections, i.e., the remaining segments not in the corrections list.
4. **PROGRESSIVE ACCEPTANCE CRITERIA (CONVERGENCE)**:
   - **STRICT CONVERGENCE**: If an error was corrected, do not look for minimal stylistic flaws in the same segment. Prioritize CLOSING the audit.
   - Round 2: Accept partial improvements in STR if there is visible progress. Only recidivate STR if the scene remains completely static.
   - Round 3 (FINAL): If technical errors (FRM/ACT/DLG) have been cleared and the structure has at least minimal conflict, GIVE SCORE 100 and issues: []. **It is FORBIDDEN to generate an infinite loop of corrections.**
5. **MANDATORY CONVERGENCE**: The audit cycle MUST close now. Avoid nitpicking. If the script is functional and professional, validate it.
`;
        } else {
            modeInstruction = `
### MODE: TOTAL EXHAUSTIVE SCAN (Round 1 — 100% Error Detection)
[LOGIC KERNEL: MASTER SCAN ACTIVE]
1. This is a total MASTER SCENE AUDIT. You MUST be ABSOLUTELY EXHAUSTIVE and RELENTLESS.
2. **ZERO TOLERANCE**: Identify ALL formatting (FRM), action (ACT), dialogue (DLG), and structural (STR — McKee/Field) violations in a single pass.
3. **CRITICAL PRIORITY**: 
    - **DLG-03 (Action in Dialogue)**: Scan every speech block. If there is an action description, report it.
    - **ACT-13 (Novelization)**: Report any literary narration, use of bold/italics in action, or narrator commentary.
4. **DO NOT SUMMARIZE**: If there are 50 errors, you MUST list 50. The success of this audit depends on finding EVERYTHING now to avoid extra rounds.
5. **THINKING STEP**: Before generating the JSON, mentally review each line of the script against the active codes list.
`;
        }

        // Segment-based Exclusion Logic (legacy, kept as backup)
        const exclusionList = verifiedSegments.length > 0
            ? `### EXCLUSION LIST (DO NOT AUDIT THESE SEGMENTS)
The following paragraphs have ALREADY been audited and verified.
It is STRICTLY FORBIDDEN to report ANY error in these specific segments:
${verifiedSegments.slice(-100).map(s => `- "${s.substring(0, 150)}..."`).join('\n')}
`
            : "";

        // RICH LOCALIZED KNOWLEDGE BASE — now imported from MasterSceneKnowledge.ts
        // getAuditKnowledge(lang) returns: Gold Standard + Checklist + Blacklist

        const prompt = `
# SCRIPT AUDIT SYSTEM (Master Scenes Standard)

[AUTEUR.OS CORE PHILOSOPHY - 27 PILLARS]
Use these 27 pillars to analyze, structure, and critique stories:
${AUTEUR_PILLARS_KNOWLEDGE_EN}

## ROLE & OBJECTIVE
${getRole(appLanguage, format)}

${getCriticalRules(format)}

${modeInstruction}

${auditContext}

${correctionsMemory}

# CRITICAL: EXCLUSION LIST (DO NOT REPORT THESE)
Our system handles these automatically. IGNORE them completely:
- Formatting of margins, indentation, fonts, page numbers, or scene numbers (FRM-07).
- Title page labels, "TITLE:" prefixes, contact info, or credits line (FRM-03, FRM-09, FRM-11, FRM-12).
- Capitalization of objects or sounds.
- Any technical margin/alignment issues.

${getAuditKnowledge(appLanguage, format)}

[STRICT FORMATTING SHIELD]
${getFormattingRules('en')}

# MANDATORY CORE AUDIT PROTOCOL (ZERO TOLERANCE):
1. **Action inside Dialogue (DLG-03)**: CRITICAL PRIORITY. Flag any physical action descriptions inside speech.
   - **CRITICAL FALSE POSITIVE PREVENTER**: ONLY flag if the text contains 3rd-person physical actions explicitly performed by characters (e.g., "He stands up", "Takes the glass") mixed inside the dialogue.
   - **THE SPOKEN WORD EXCEPTION**: NEVER flag actions that are SPOKEN by the character as part of their speech (e.g., "The wind turns", "The current pulls", "I will run"). If the character is talking about nature, feelings, or future actions, IT IS 100% CORRECT. DO NOT FLAG.
   - **BLOCK CAPTURE RULE**: For DLG-03, your 'original' snippet MUST include the Character Name line + the Dialogue line.
2. **Novelization (ACT-13)**: Flag any prose-like narration, bold/italicized text in action blocks, or narrator commentary. Script action must be present tense and objective.
3. **Time & Light Consistency (FRM-15)**: Cross-reference Heading time (DAY/NIGHT) with action. Flag contradictions (e.g. NIGHT heading with "bright sunlight" in text).
4. **Character Introduction (CHR-03)**: First time a character appears, name MUST be in UPPERCASE.
5. **Logic (LOG-01/LOG-02)**: Flag object disappearance or impossible spatial movements.

            # SYSTEMATIC ANALYSIS PROTOCOL:
    1. **Heading Check**: Matches action duration? (FRM-15)
    2. **Dialogue Check**: Does it contain third-person verbs like "walks", "looks", "smiles"? (DLG-03)
    3. **Action Check**: Present tense? No bold/italics? (ACT-13)
    4. **Structure Check**: Is there a conflict? (STR-01)

        ${methodologySection}

            # YOUR MISSION
            Analyze the script content and report ALL violations.
            **PRIORITY 1: TECHNICAL INTEGRITY (DLG-03, ACT-13, FRM-15)**
            **PRIORITY 2: STRUCTURAL ANALYSIS (STR CODES)**

            CRITICAL RULE: Report EVERY SINGLE INSTANCE. If the same error happens 10 times, report 10 issues.
            DO NOT SUMMARIZE. DO NOT SKIP.
            If this is Round 1, your goal is a 100% EXHAUSTIVE SCAN. Give no mercy.

        ${exclusionList}

[OFFICIAL AUDIT CODES - USE ONLY THESE]:
            ${activeErrors.map(err => `- [${err.code}] ${err.title}: ${err.description}`).join('\n')}

            CRITICAL HALLUCINATION CHECK:
- DO NOT flag short action lines as "Long Block". Only 5 + lines.
            - DO NOT report any issue that can be fixed by just changing capitalization or punctuation.
            - DO NOT flag inverted location hierarchy.
            - If an Action Line is visually descriptive (e.g. "He walks fast"), DO NOT FLAG IT as unfilmable. (Note: This does NOT apply to DLG-03; actions inside dialogue must always be flagged).

            # SCORE CALCULATION:
- Start with 100.
- Subtract 5 points for each CRITICAL technical error.
            - Subtract 0 points for stylistic suggestions.
            - If no critical issues are reported, score MUST be 100.

            STRICT OUTPUT RULES:
- Return ONLY a JSON object.
            - The "issues" array MUST ONLY contain violations that are NOT in the "EXCLUSION LIST".
            - If only excluded issues exist, return score: 100 and issues: [].
            - JSON structure:
{
    "score": [number 0 - 100],
        "issues": [
            {
                "line": [approximate line number],
                "original": "[EXACT snippet from the script to be replaced. For DLG-03, MUST include the Character Name + Dialogue line]",
                "suggestion": "[Corrected replacement. For DLG-03, split into: CHARACTER \\n DIALOGUE \\n \\n ACTION \\n \\n CHARACTER (CONT'D) \\n DIALOGUE]",
                "rule": "[Error Code]"
            }
        ]
}

- Translate "suggestion" content to ${targetLangDescription}.
            - ** CONSOLIDATION RULE **: If a single line has multiple violations, consolidate them.
            - ** DLG-03 FULL BLOCK RULE **: When fixing DLG-03, your 'original' MUST include the Character Name + the faulty Dialogue. Your 'suggestion' MUST be a multi-block Fountain structure:
              1. The Character Name
              2. The speech line
              3. A BLANK LINE
              4. The Action line (Scene Action)
              5. (If speech continues) A BLANK LINE, The Character Name with (CONT'D), and the rest of the speech.
            **This separation is MANDATORY. Do NOT keep action verbs inside dialogue blocks.**
            
            SCRIPT CONTENT TO AUDIT(Each line is prefixed with its absolute line number):
            ${scriptContent.split('\n').map((line, i) => `[Line ${startLine + i}]: ${line}`).join('\n')}
`;

        const modelName = await getBestModel(ai);
        const response = await ai.models.generateContent({
            model: modelName,
            contents: [{ role: 'user', parts: [{ text: prompt }] }],
            config: {
                temperature: temperature,
                safetySettings: getSafetySettings(config)
            }
        });

        const rawText = response.text || "";

        try {
            // SAFE-CHECK: Post-process filtering to ensure no silent or hallucinated codes leak
            const jsonStart = rawText.indexOf('{');
            const jsonEnd = rawText.lastIndexOf('}') + 1;
            if (jsonStart !== -1 && jsonEnd !== -1) {
                const jsonStr = rawText.substring(jsonStart, jsonEnd);
                const data = JSON.parse(jsonStr);

                if (data.issues && Array.isArray(data.issues)) {
                    const initialLength = data.issues.length;
                    const seen = new Set<string>();
                    const activeCodes = activeErrors.map(e => e.code.replace(/[^A-Z0-9]/g, ''));

                    data.issues = data.issues.filter((issue: any) => {
                        // 0. Clean numeric line
                        const lineStr = String(issue.line || "0");
                        const lineMatch = lineStr.match(/\d+/);
                        const cleanLine = lineMatch ? parseInt(lineMatch[0]) : 0;
                        issue.line = cleanLine;

                        // 0.1 Clean "original" snippet
                        issue.original = (issue.original || "").toString().replace(/^\[Line \d+\]:\s*/i, "").replace(/^L\d+:\s*/i, "").trim();

                        // 0.1b Clean "suggestion" snippet
                        issue.suggestion = (issue.suggestion || "").toString().trim();

                        // 0.2 Normalize Rule Code
                        const rawRule = (issue.rule || "").toUpperCase();
                        let normalizedRule = rawRule.replace(/[^A-Z0-9]/g, '');
                        const ruleMatch = rawRule.match(/([A-Z]{3}-\d{2})/);
                        if (ruleMatch) {
                            normalizedRule = ruleMatch[1].replace(/[^A-Z0-9]/g, '');
                        }

                        // 1. STRICT INCLUSION: ONLY allow codes that were sent to the AI as "active"
                        if (!activeCodes.includes(normalizedRule)) {
                            return false;
                        }

                        // 2. Map back to official standard code for UI consistency
                        const standardRule = activeErrors.find(e => e.code.replace(/[^A-Z0-9]/g, '') === normalizedRule);
                        issue.rule = standardRule ? standardRule.code : rawRule;

                        const lineKey = String(cleanLine || "General");
                        const originalSnippet = (issue.original || "").trim();
                        // Comparison key ignores subtle formatting differences (dashes, spaces, etc)
                        const comparisonSnippet = (issue.suggestion || "").toUpperCase().replace(/[^A-Z0-9]/g, '');
                        // NEW CONSOLIDATION KEY: Same line + Same Suggestion = Merge
                        const key = `${lineKey} -${comparisonSnippet} `;

                        // 3. Remove if it's a duplicate
                        if (seen.has(key)) return false;
                        seen.add(key);

                        // 4. TRIVIAL/HALLUCINATION CHECK: If suggestion is identical to original, DISCARD.
                        const cleanOriginal = originalSnippet.toLowerCase().replace(/[^a-z0-9]/g, '');
                        const cleanSuggestion = (issue.suggestion || "").toLowerCase().replace(/[^a-z0-9]/g, '');
                        if (cleanOriginal === cleanSuggestion) {
                            return false;
                        }

                        // 🛡️ DLG-03 FALSE POSITIVE DESTROYER 🛡️
                        if (normalizedRule === 'DLG-03') {
                            // Se a sugestão não contém uma dupla quebra de linha (separando a ação), a IA alucinou.
                            if (!issue.suggestion.includes('\\n\\n') && !issue.suggestion.includes('\n\n')) {
                                console.warn(`[Audit] Blocked DLG-03 Hallucination: AI tried to split dialogue without creating an action block.`);
                                return false; // Descarta o erro silenciosamente
                            }
                        }

                        // 5. PROTECTION CHECK: Pre-normalize segments if not already done (implicitly)
                        // Optimization: Skip if verifiedSegments is empty
                        if (verifiedSegments.length === 0) return true;

                        const isVerified = verifiedSegments.some(vs => {
                            const cleanVS = String(vs || "").toLowerCase().replace(/[^a-z0-9]/g, '');
                            // Match if exactly equal, or if one is a significant part of the other (min 80% overlap or substantial substring)
                            if (cleanOriginal === cleanVS) return true;
                            if (cleanVS.length > 5 && cleanOriginal.length > 5) {
                                return cleanOriginal.includes(cleanVS) || cleanVS.includes(cleanOriginal);
                            }
                            return false;
                        });
                        if (isVerified) return false;

                        return true;
                    });

                    // 🛡️ DETERMINISTIC SCORING ENGINE (v3 - Severity Aware)
                    let penaltyPoints = 0;
                    data.issues.forEach((issue: any) => {
                        const ruleCode = cleanCode(issue.rule || "");
                        const ruleDef = AUDIT_ERRORS[ruleCode];

                        // If round is high (3+), only penalize ERRORS, not suggestions
                        // Even better: suggestions NEVER penalize.
                        if (ruleDef) {
                            if (ruleDef.severity === 'error') {
                                if (ruleCode.startsWith('FRM')) penaltyPoints += 5;
                                else if (ruleCode.startsWith('ACT')) penaltyPoints += 3;
                                else penaltyPoints += 2;
                            } else {
                                // Severity is 'suggestion' -> 0 points
                                issue.type = 'suggestion';
                            }
                        } else {
                            // Unknown rule -> play safe
                            penaltyPoints += 1;
                        }
                    });

                    // 🎯 CONVERGENCE LOGIC: Progressive leniency
                    // Round 2: reduce penalty by 50% for remaining issues (corrections were applied)
                    // Round 3+: only count truly new errors, not residual suggestions
                    if (round >= 3) {
                        // Final round: only penalize genuine errors, not suggestions
                        data.issues = data.issues.filter((issue: any) => {
                            const ruleDef = AUDIT_ERRORS[cleanCode(issue.rule || "")];
                            return ruleDef?.severity === 'error';
                        });
                        penaltyPoints = data.issues.length * 3; // Mild penalty for genuine remaining errors
                    } else if (round === 2) {
                        penaltyPoints = Math.ceil(penaltyPoints * 0.5);
                    }

                    const finalScore = Math.max(0, 100 - penaltyPoints);
                    data.score = data.issues.length === 0 ? 100 : finalScore;
                }
                return {
                    content: JSON.stringify(data),
                    debugLog: rulesSummary + "\n" + rawText
                };
            }
        } catch (e) {
            console.error("Error parsing audit JSON:", e);
        }

        return {
            content: rawText,
            debugLog: rulesSummary + "\n" + rawText
        };
    });
};

export const reviewScript = async (
    scriptContent: string,
    appLanguage: string,
    config?: AppConfig,
    format: string = 'Film',
    onProgress?: (progress: ReviewProgressState) => void,
    signal?: AbortSignal,
    lineToBlockMap?: Map<number, number>
): Promise<{ score: number, issues: any[] }> => {
    return retryWithBackoff(async () => {
        const ai = await getAIInstance();

        // 1. Technical/Aesthetic rules handled by Mechanical Linter OR Subjective rules that cannot be auto-patched
        const excludedRules = new Set([
            ...MECHANICAL_RULES_EXCLUSION,
            // Regras de Estrutura (Exigem reescrita criativa completa - PROIBIDO AUTO-FIX)
            'STR-01', 'STR-02', 'STR-03', 'STR-04',
            // Regras de Estilo e Narrativa (Exigem alteração da voz do autor - PROIBIDO AUTO-FIX)
            'ACT-03', 'ACT-04', 'ACT-05', 'ACT-12', 'ACT-13', 'ACT-15', 'ACT-17', 'ACT-18', 'ACT-19', 'ACT-20', 'ACT-21', 'ACT-22',
            'DLG-01', 'LOG-01', 'LOG-02', 'FRM-15',
            // Regras de Personagem e Finalização que exigem INVENÇÃO de ação (PROIBIDO AUTO-FIX)
            'CHR-03', 'CHR-04', 'FRM-17', 'DLG-04'
        ]);

        const localizedMap = LOCALIZED_ERRORS[appLanguage] || AUDIT_ERRORS;
        const englishMap = AUDIT_ERRORS_EN;
        const activeRulesDescription = Object.values(englishMap)
            .filter(err => !excludedRules.has(err.code))
            .map(err => `- **${err.code} (${err.title})**: ${err.description}. Fix: ${err.fixPrompt}`)
            .join('\n');

        // 2. Segmenter with Line Numbers
        const lines = scriptContent.split('\n');
        const scenes: { text: string; startLine: number }[] = [];
        let currentSceneLines: string[] = [];
        let sceneStartLine = 1;
        const sceneRegex = /^(?:INT\.|EXT\.|I\/E|INT\.\/EXT\.)/i;

        lines.forEach((line, idx) => {
            if (sceneRegex.test(line.trim())) {
                if (currentSceneLines.length > 0) {
                    scenes.push({
                        text: currentSceneLines.map((l, i) => `[L-${sceneStartLine + i}]: ${l}`).join('\n'),
                        startLine: sceneStartLine
                    });
                }
                currentSceneLines = [line];
                sceneStartLine = idx + 1;
            } else {
                currentSceneLines.push(line);
            }
        });

        if (currentSceneLines.length > 0) {
            scenes.push({
                text: currentSceneLines.map((l, i) => `[L-${sceneStartLine + i}]: ${l}`).join('\n'),
                startLine: sceneStartLine
            });
        }

        const allIssues: any[] = [];
        const BATCH_SIZE = 4; // Processa 4 cenas em paralelo para maximizar velocidade
        let consecutiveFailures = 0;
        const MAX_CONSECUTIVE_FAILURES = 3;

        // UPGRADE: Force PRO model for structural auditing if at all possible.
        const modelName = await getBestModel(ai); // Usa o modelo Flash (mais rápido) para caça a erros técnicos

        for (let i = 0; i < scenes.length; i += BATCH_SIZE) {
            if (signal?.aborted) {
                console.log("[reviewScript] Abort requested. Finalizing review early.");
                break;
            }
            const batch = scenes.slice(i, i + BATCH_SIZE);

            // Report progress: INITIAL STATE (Context Prep)
            if (onProgress) {
                const currentBatchHeading = batch[0]?.text.split('\n')[0].replace(/^\[L-\d+\]:\s*/, '').trim() || "Scene";
                const preparingPrefix = PREPARING_CONTEXT[appLanguage] || PREPARING_CONTEXT['en'] || PREPARING_CONTEXT['pt'];
                onProgress({
                    analyzedScenesCount: i + 1,
                    totalScenes: scenes.length,
                    currentProcessingHeading: currentBatchHeading,
                    statusMessage: `${preparingPrefix} ${currentBatchHeading}`
                });
            }

            const promises = batch.map(async (scene, batchIdx) => {
                const globalIndex = i + batchIdx;
                const prevContext = scenes.slice(Math.max(0, globalIndex - 2), globalIndex)
                    .map((s) => `--- PREVIOUS CONTEXT ---\n${s.text}`).join('\n\n');

                const contextString = globalIndex === 0
                    ? "[FIRST SCENE OF THE SCRIPT - NO PRIOR CONTEXT EXISTS.]"
                    : prevContext;

                const systemPrompt = [
                    'You are a STRICT Formatting Surgeon auditing a Master Scene screenplay.',
                    'Your ONLY job is to fix hard technical formatting violations.',
                    'DO NOT act as a creative editor. DO NOT fix style, pacing, prose, or grammar.',
                    '',
                    'THE "PERFECT SCRIPT" DIRECTIVE (CRITICAL):',
                    '- LLMs naturally want to "fix" things. YOU MUST RESIST THIS URGE.',
                    '- If the text has no blatant formatting errors from the Active Rules list below, IT IS PERFECT.',
                    '- If the scene is perfect, YOU MUST RETURN AN EMPTY ARRAY: []',
                    '- Zero issues is the EXPECTED result. Do NOT invent issues just to return a patch.',
                    '',
                    'LANGUAGE PRESERVATION (MANDATORY):',
                    '- You MUST detect and preserve the ORIGINAL LANGUAGE of the script content.',
                    '- ALL suggested_text corrections MUST be in the exact same language as the original script.',
                    '- NEVER translate the script content. The review is about STRUCTURE, not translation.',
                    `- The "explanation" field MUST be written in ${LANGUAGE_MAP[appLanguage] || 'English'} (the user interface language).`,
                    '',
                    'CRITICAL CONSTRAINTS (TOP PRIORITY):',
                    '1. THE BREAK RULE (DLG-03) IS NARROW: ONLY flag if a character\'s speech block explicitly contains 3rd-person PHYSICAL ACTION VERBS (e.g., "John grabs the gun").',
                    '2. THE SPOKEN WORD EXCEPTION (CRITICAL): If the character is verbally speaking about an action (e.g. "The current can pull you", "I will punch him"), THAT IS DIALOGUE. IT IS NOT AN ERROR. Do not flag verbs that are part of the spoken sentence.',
                    '3. SURGICAL PATCHES: Generate isolated patches only for the active rules.',
                    '4. NO CAVEMAN GRUNTS (FATAL ERROR): NEVER inject fake vocalizations like "Grrr!", "Uh." or any other sounds into a character\'s dialogue.',
                    '5. PRESERVE DIALOGUE: When fixing DLG-03, you MUST keep the character\'s spoken words exactly as they are. Just move the 3rd-person physical action to a new line.',
                    '6. DO NOT OVER-AUDIT: If a rule is NOT in the "ACTIVE RULES" list below, ignore it entirely.',
                    '7. ANTI-DUPLICATION (FATAL ERROR): NEVER duplicate the original text inside the suggested_text. Output the final version ONCE.',
                    '8. NO PROSE CAPITALIZATION: NEVER convert a normal action sentence into ALL CAPS.',
                    '9. STRICT LANGUAGE LOCK: NEVER translate script terms. If the text says "A MONTAGEM", keep it EXACTLY as "A MONTAGEM".',
                    '10. NO OVERLAPPING PATCHES: CONSOLIDATE multiple errors in the same block into a SINGLE patch.',
                    '11. FALSE POSITIVE SHIELD: Metaphorical speech, storytelling, or philosophical dialogue is NOT physical action. Leave it alone.',
                    '12. FOUNTAIN SPACING (FATAL ERROR): In your suggested_text, you MUST use double newlines (\\n\\n) to separate an Action line from a Character Name. A single newline (\\n) merges them into a corrupted action paragraph.',
                    '',
                    '[STRICT OUTPUT SYNTAX ONLY]',
                    '- Scene Headings and Character Names MUST be ALL CAPS.',
                    '- Action and Dialogue MUST be in standard sentence case (lowercase).',
                    '- CRITICAL: If an action line starts with a character name, DO NOT capitalize the entire line. ONLY the name is capitalized.',
                    '- NEVER write a character name multiple times in a row.',
                    '- NEVER apply general formatting rules outside of the ACTIVE RULES list.',
                    '',
                    'SURGICAL MATCHING RULE:',
                    '- Your original_text must be a LITERAL match of the lines in the script, including line number prefixes like [L-XX]:.',
                    '- You MUST preserve exact line breaks (\\n) in original_text.',
                    '- For DLG-03, your original_text MUST START at the Character Name line.',
                    '',
                    'AUDIT REASONING PROTOCOL:',
                    'For EVERY element in the TARGET SCENE, ask:',
                    '1. DLG-03 CHECK: Is this text block formatted as DIALOGUE but describes physical movement in 3rd person? If YES: FLAG DLG-03. If it is just a character speaking about actions, IGNORE.',
                    '2. ACTIVE RULES CHECK: Does it violate any other rule from the explicit list below? If YES: FLAG it.',
                    '',
                    'OUTPUT FORMAT (STRICT JSON):',
                    '- If you find valid formatting errors, return the array of patch objects.',
                    '- If the scene is well-formatted, return EXACTLY: []',
                    '[',
                    '  {',
                    '    "error_code": "RULE_CODE",',
                    '    "original_text": "EXACT segment/lines, INCLUDING the [L-XX]: prefix",',
                    '    "suggested_text": "Corrected text. Do NOT include line numbers here",',
                    '    "explanation": "Explanation in the UI language"',
                    '  }',
                    ']',
                    '',
                    'ACTIVE RULES:',
                    activeRulesDescription
                ].join('\n');

                const userPrompt = `
PREVIOUS CONTEXT:
${contextString}

TARGET SCENE TO AUDIT (WITH LINE ANCHORS):
${scene.text}
`;
                // --- ROTATING STATUS MESSAGES ---
                let stageIdx = 0;
                const sceneHeading = scene.text.split('\n')[0].replace(/^\[L-\d+\]:\s*/, '').trim() || `Cena ${globalIndex + 1}`;

                const updateStatus = () => {
                    if (onProgress) {
                        if (signal?.aborted) {
                            onProgress({
                                analyzedScenesCount: i + 1,
                                totalScenes: scenes.length,
                                currentProcessingHeading: sceneHeading,
                                statusMessage: TRANSLATIONS[appLanguage as keyof typeof TRANSLATIONS]?.finishing || "finalizando..."
                            });
                            return;
                        }

                        const stages = ANALYSIS_STAGES[appLanguage] || ANALYSIS_STAGES['en'] || ANALYSIS_STAGES['pt'];
                        const baseMessage = stages[stageIdx % stages.length];

                        onProgress({
                            analyzedScenesCount: i + 1,
                            totalScenes: scenes.length,
                            currentProcessingHeading: sceneHeading,
                            statusMessage: `${baseMessage} ${sceneHeading}`
                        });
                        stageIdx++;
                    }
                };

                // Initial call
                updateStatus();
                // Set interval to rotate every 1.5s
                const statusInterval = setInterval(updateStatus, 1500);

                try {
                    const response = await ai.models.generateContent({
                        model: modelName,
                        contents: [{ role: 'user', parts: [{ text: userPrompt }] }],
                        config: {
                            systemInstruction: systemPrompt,
                            temperature: 0.2,
                            responseMimeType: "application/json",
                            safetySettings: getSafetySettings(config)
                        }
                    });

                    clearInterval(statusInterval);

                    const rawText = response.text || "[]";
                    const patches = JSON.parse(rawText);
                    consecutiveFailures = 0; // Reset on success

                    if (Array.isArray(patches)) {
                        // 🛡️ FILTRO DE ALUCINAÇÃO (FAILSAFE) 🛡️
                        const validPatches = patches.filter((patch: any) => {
                            if (!patch.original_text || !patch.suggested_text) return false;

                            const cleanOrig = patch.original_text.replace(/^\[L-\d+\]:\s*/gm, "").toLowerCase().replace(/[^a-z0-9]/g, '');
                            const cleanSugg = (patch.suggested_text || "").replace(/^\[L-\d+\]:\s*/gm, "").toLowerCase().replace(/[^a-z0-9]/g, '');

                            // Se a sugestão for matematicamente idêntica ao original, a IA alucinou. DESCARTAR.
                            if (cleanOrig === cleanSugg) {
                                console.warn(`[reviewScript] Blocked Hallucination: AI flagged ${patch.error_code} but suggested identical text.`);
                                return false;
                            }
                            return true;
                        });

                        return validPatches.map((patch: any) => {
                            const errorCode = (patch.error_code || "").toUpperCase();
                            const ruleDef = (localizedMap as any)[errorCode];

                            const lineNumbers: number[] = [];
                            const lineRegex = /^\[L-(\d+)\]:/gm;
                            let lineMatch;
                            while ((lineMatch = lineRegex.exec(patch.original_text)) !== null) {
                                lineNumbers.push(parseInt(lineMatch[1], 10));
                            }

                            const targetBlockIndices: number[] = [];
                            if (lineToBlockMap && lineNumbers.length > 0) {
                                const seenIndices = new Set<number>();
                                for (const ln of lineNumbers) {
                                    const blockIdx = lineToBlockMap.get(ln);
                                    if (blockIdx !== undefined && !seenIndices.has(blockIdx)) {
                                        seenIndices.add(blockIdx);
                                        targetBlockIndices.push(blockIdx);
                                    }
                                }
                            }

                            const cleanOriginal = patch.original_text.replace(/^\[L-\d+\]:\s*/gm, "");
                            const cleanSuggestion = (patch.suggested_text || "").replace(/^\[L-\d+\]:\s*/gm, "");

                            return {
                                rule: errorCode,
                                original: cleanOriginal,
                                suggestion: cleanSuggestion,
                                explanation: patch.explanation,
                                line: lineNumbers[0] || 0,
                                type: ruleDef ? ruleDef.severity : 'suggestion',
                                title: ruleDef ? ruleDef.title : 'Suggested Revision',
                                target_block_indices: targetBlockIndices
                            };
                        });
                    }
                } catch (e: any) {
                    clearInterval(statusInterval);
                    consecutiveFailures++;
                    console.error(`[reviewScript] Scene Error ${globalIndex} (Attempt ${consecutiveFailures}/${MAX_CONSECUTIVE_FAILURES}):`, e);

                    if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
                        const msg = e?.message || "Internal AI Service Error (500)";
                        throw new Error(`Review stopped: Multiple consecutive failures from AI service. ${msg}`);
                    }
                }
                return [];
            });

            const batchResults = await Promise.all(promises);
            allIssues.push(...batchResults.flat());
        }

        // Final progress update
        if (onProgress) {
            onProgress({
                analyzedScenesCount: scenes.length,
                totalScenes: scenes.length,
                currentProcessingHeading: "Concluído",
                statusMessage: "Finalizando auditoria..."
            });
        }

        // 3. DETERMINISTIC MECHANICAL AUDIT (Rules that need no AI reasoning)
        const SCREENPLAY_TRANSITIONS = /^(FADE OUT\.|FADE IN:|FADE TO:|CUT TO:|SMASH CUT TO:|MATCH CUT TO:|DISSOLVE TO:|THE END\.|FIM\.)$/i;
        const SCREENPLAY_EXTENSION = /^\((V\.O\.|O\.S\.|CONT'D|CONT\u2019D)\)/i;
        const isCharacterName = (text: string) =>
            text === text.toUpperCase() &&
            !sceneRegex.test(text) &&
            !SCREENPLAY_TRANSITIONS.test(text) &&
            !SCREENPLAY_EXTENSION.test(text) &&
            text.trim().length > 0 &&
            !/[.!?]$/.test(text.trim()); // Character names don't end in punctuation

        // FRM-13/01: Headings not in ALL CAPS
        lines.forEach((line, idx) => {
            const lineNum = idx + 1;
            const trimmed = line.trim();
            if (!trimmed) return;

            if (sceneRegex.test(trimmed) && trimmed !== trimmed.toUpperCase()) {
                allIssues.push({
                    rule: 'FRM-13',
                    original: trimmed,
                    suggestion: trimmed.toUpperCase(),
                    line: lineNum,
                    type: 'error',
                    title: localizedMap['FRM-13']?.title || 'Heading Case',
                    explanation: localizedMap['FRM-13']?.description || '',
                    target_block_indices: lineToBlockMap ? [lineToBlockMap.get(lineNum)].filter((v): v is number => v !== undefined) : []
                });
            }

            // FRM-19: Markdown Residue
            if (line.includes('**') || line.includes('```') || line.includes('___')) {
                allIssues.push({
                    rule: 'FRM-19',
                    original: line,
                    suggestion: line.replace(/\*\*|```|___/g, ''),
                    line: lineNum,
                    type: 'error',
                    title: localizedMap['FRM-19']?.title || 'Markdown',
                    explanation: localizedMap['FRM-19']?.description || '',
                    target_block_indices: lineToBlockMap ? [lineToBlockMap.get(lineNum)].filter((v): v is number => v !== undefined) : []
                });
            }

            // DLG-06: Dialogue on same line as parenthetical
            if (trimmed.startsWith('(') && trimmed.includes(')') && trimmed.match(/\)\s*[A-Za-z0-9]/)) {
                allIssues.push({
                    rule: 'DLG-06',
                    original: trimmed,
                    suggestion: trimmed.replace(/\)(\s*)(?=[A-Za-z0-9])/, ')\n'),
                    line: lineNum,
                    type: 'error',
                    title: localizedMap['DLG-06']?.title || 'Dialog Shift',
                    explanation: localizedMap['DLG-06']?.description || '',
                    target_block_indices: lineToBlockMap ? [lineToBlockMap.get(lineNum)].filter((v): v is number => v !== undefined) : []
                });
            }
        });

        const penalty = allIssues.length * 5;
        return {
            score: Math.max(10, 100 - penalty),
            issues: allIssues
        };
    });
};

export const cleanScriptWhitespace = (
    text: string,
    scriptMode: string = 'Film',
    language: string = 'en',
    isFirstDraft: boolean = false
): string => {
    if (!text) return '';

    let cleaned = text;

    // 🛡️ NORMALIZE EXTENSIONS FIRST (Crucial para a estabilidade do Parser) 🛡️
    // Conserta os erros de digitação da IA como (CONTD) ou ( V.O ) para o padrão exato Fountain.
    cleaned = cleaned.replace(/\(\s*CONTD\.?\s*\)/gi, "(CONT'D)");
    cleaned = cleaned.replace(/\(\s*CONT['’]D\.?\s*\)/gi, "(CONT'D)");
    cleaned = cleaned.replace(/\(\s*V\.?O\.?\s*\)/gi, "(V.O.)");
    cleaned = cleaned.replace(/\(\s*O\.?S\.?\s*\)/gi, "(O.S.)");

    // 🛡️ PRE-PROCESSING 1: COSTURA DE PARÊNTESES QUEBRADOS 🛡️
    cleaned = cleaned.replace(/\n\s*(\([^)]*)\n([^()]*\))/g, ' $1 $2');

    // 🛡️ PRE-PROCESSING 2: PROTEÇÃO DE PARÊNTESES NA MESMA LINHA 🛡️
    cleaned = cleaned.replace(/^([A-Z0-9À-ÖØ-Þ\s\.\-]+)\s+(\([^A-Z][^)]+\))$/gm, '$1\n$2');

    // 🛡️ TRIM UNIVERSAL (MATA ESPAÇOS FANTASMAS DA IA) 🛡️
    // O trim() puro aqui garante que a IA não coloque espaços de indentação antes do diálogo ou rubrica.
    // Espaços antes da linha quebram a árvore de sintaxe do Fountain e geram os parágrafos errados.
    let cleanedLines = cleaned.split('\n').map(line => line.trim());

    const SCENE_HEADING_REGEX = /^(?:INT\.|EXT\.|I\/E|INT\.\/EXT\.)/i;
    const SCREENPLAY_TRANSITIONS_REGEX = /^(FADE OUT\.|FADE IN:|FADE TO:|CUT TO:|SMASH CUT TO:|MATCH CUT TO:|DISSOLVE TO:|THE END\.|FIM\.)/i;
    const SCREENPLAY_EXTENSION_REGEX = /\s*\((V\.O\.|O\.S\.|CONT'D|CONT\u2019D|VOZ|OFF)\)/i;

    // 🛡️ PASS 0: NORMALIZADOR DE NOMES QUEBRADOS 🛡️
    for (let i = 0; i < cleanedLines.length - 1; i++) {
        let line = cleanedLines[i];

        if (line.length > 0 && line.length < 60 && !/[.!?]$/.test(line) && !SCENE_HEADING_REGEX.test(line) && !SCREENPLAY_TRANSITIONS_REGEX.test(line)) {
            let nextLine = cleanedLines[i + 1];

            if (nextLine.startsWith('(')) {
                if (!line.includes("CONT'D")) {
                    cleanedLines[i] = line.replace(/["']/g, '').toUpperCase();
                }
            }
            else if (line.includes('"') || line.includes("'")) {
                if (!line.includes("CONT'D")) {
                    let firstWord = line.split(' ')[0];
                    if (firstWord === firstWord.toUpperCase() && /[A-ZÀ-ÖØ-Þ]/.test(firstWord)) {
                        cleanedLines[i] = line.replace(/["']/g, '').toUpperCase();
                    }
                }
            }
        }
    }

    const isChar = (str: string) => {
        if (str.length === 0) return false;
        if (SCENE_HEADING_REGEX.test(str)) return false;
        if (SCREENPLAY_TRANSITIONS_REGEX.test(str)) return false;
        if (/[.!?]$/.test(str)) return false;

        const namePart = str.replace(SCREENPLAY_EXTENSION_REGEX, '').trim();
        return namePart === namePart.toUpperCase() && namePart.length > 0 && /[A-ZÀ-ÖØ-Þ]/.test(namePart);
    };

    // 🛡️ PASS 1: FORÇAR LINHA EM BRANCO ANTES DE PERSONAGENS 🛡️
    for (let i = 1; i < cleanedLines.length; i++) {
        const line = cleanedLines[i];
        const prevLine = cleanedLines[i - 1];
        if (isChar(line) && prevLine.length > 0) {
            cleanedLines.splice(i, 0, '');
            i++;
        }
    }

    // 🛡️ PASS 2: ORPHAN CUE DESTROYER 🛡️
    const escapeRegExp = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    for (let i = 0; i < cleanedLines.length - 1; i++) {
        const line = cleanedLines[i];
        if (isChar(line)) {
            let charName = line.replace(SCREENPLAY_EXTENSION_REGEX, '').trim();

            let nextIdx = i + 1;
            while (cleanedLines[nextIdx] && cleanedLines[nextIdx].startsWith('(')) {
                nextIdx++;
            }

            let suspectLine = cleanedLines[nextIdx];
            if (suspectLine && suspectLine.length > 0) {
                const actionRegex = new RegExp(`^(O |A |Os |As |Um |Uma )?${escapeRegExp(charName)}\\b`, 'i');
                const pronounActionRegex = /^(Ele|Ela|Eles|Elas)\s+(caminha|olha|pega|corre|vai|começa|se |sorri|suspira|empurra|arrasta|levanta|senta|grita|chora|avança|recua|gira|tenta|aproxima|afasta)/i;
                const verbActionRegex = /^(Se aproxima|Aproxima-se|Vira|Olha|Caminha|Corre|Grita|Senta|Levanta|Afasta|Puxa)\\b/i;

                if (
                    actionRegex.test(suspectLine) ||
                    pronounActionRegex.test(suspectLine) ||
                    verbActionRegex.test(suspectLine) ||
                    suspectLine.toLowerCase().startsWith('se ')
                ) {
                    cleanedLines.splice(nextIdx, 0, '');
                }
            }
        }
    }

    cleaned = cleanedLines.join('\n');

    // 🛡️ FILTRO ANTI-GAGUEIRA (DETERMINÍSTICO E AGRESSIVO) 🛡️
    // Captura nomes duplicados independentemente de espaços ou tabs invisíveis antes do Enter
    const nameStackRegex = /^([A-Z0-9À-ÖØ-Þ \.\-\(\)\'’]+)\s*\n(?:\1\s*\n)+/gm;
    cleaned = cleaned.replace(nameStackRegex, '$1\n');

    cleaned = cleaned.replace(/\n{3,}/g, '\n\n').trim();

    return cleaned;
};

export interface PlotFixOption {
    id: string;
    title: string;
    action_beat: string;
    insertion_point: string;
    ripple_effect: string;
}

export const generatePlotFixOptions = async (
    factList: string[],
    targetPlotHole: string,
    userLanguage: string
): Promise<PlotFixOption[]> => {
    const apiKey = "dummy-secure-proxy";

    const ai = await getAIInstance();
    const modelName = await getBestModel(ai);

    const prompt = `Act as a Script Doctor.
Based on the list of facts from the script and the specific plot hole detected ("${targetPlotHole}"), generate exactly 3 logical narrative solutions.

Solutions MUST be creative, maintain character consistency, and fix the causality gap.

OUTPUT REQUIREMENTS:
1. id: unique slug.
2. title: short creative title.
3. action_beat: what happens in this scene precisely.
4. insertion_point: where should this be added.
5. ripple_effect: what else changes in the story.

The Fact List:
${factList.map(fact => `- ${fact}`).join('\n')}

All strings MUST be written strictly in ${userLanguage}.`;

    const result = await executeWithResilience(
        async (signal) => {
            const res = await ai.models.generateContent({
                model: modelName,
                contents: [{ role: 'user', parts: [{ text: prompt }] }],
                config: {
                    temperature: 0.2,
                    responseMimeType: "application/json",
                    responseSchema: {
                        type: "object",
                        properties: {
                            options: {
                                type: "array",
                                items: {
                                    type: "object",
                                    properties: {
                                        id: { type: "string" },
                                        title: { type: "string" },
                                        action_beat: { type: "string" },
                                        insertion_point: { type: "string" },
                                        ripple_effect: { type: "string" }
                                    },
                                    required: ["id", "title", "action_beat", "insertion_point", "ripple_effect"]
                                }
                            }
                        },
                        required: ["options"]
                    }
                }
            });

            let rawText = res.text || '{"options":[]}';

            // Anti-Markdown Shield
            rawText = rawText.replace(/```json/gi, '').replace(/```/g, '').trim();

            try {
                const parsed = JSON.parse(rawText);
                return parsed.options || [];
            } catch (err) {
                console.error("generatePlotFixOptions parse error", err);
                throw new Error("Failed to parse plot fix options");
            }
        },
        { maxRetries: 3, timeoutMs: 25000 }
    );

    return result;
};

export const injectPlotFix = async (
    sceneText: string,
    chosenBeat: string,
    userLanguage: string = "Portuguese (Brazil)"
): Promise<string> => {
    const apiKey = "dummy-secure-proxy";

    const ai = await getAIInstance();
    const modelName = await getBestModel(ai);

    const prompt = `Act as an elite Ghostwriter. Inject the following narrative beat into the existing Fountain scene seamlessly.

ORIGINAL SCENE:
${sceneText}

NARRATIVE BEAT TO INJECT:
${chosenBeat}

STRICT WRITING RULES:
1. NO '(CONT'D)' next to characters.
2. NO 'POUCO DEPOIS', use sluglines (DAY/NIGHT).
3. NO '- CONTINUAÇÃO' in sluglines.
4. Physical actions ONLY, no subtext/internal thought.
5. Preserve Fountain format.

The rewritten scene MUST be in ${userLanguage}.`;

    const rewrittenScene = await executeWithResilience(
        async (signal) => {
            const res = await ai.models.generateContent({
                model: modelName,
                contents: [{ role: 'user', parts: [{ text: prompt }] }],
                config: { temperature: 0.1 }
            });

            let rawText = res.text || "";

            if (rawText.startsWith('\`\`\`')) {
                rawText = rawText.replace(/^\`\`\`(?:fountain)?\s*\n/i, '').replace(/\n\`\`\`\s*$/i, '');
            }

            return rawText;
        },
        { maxRetries: 3, timeoutMs: 25000 }
    );

    return rewrittenScene;
};
